import axios, { AxiosInstance } from 'axios';
import { 
  Order, 
  OrderStatus,
  Ingredient, 
  MenuItem, 
  PurchaseOrder,
  PurchaseOrderStatus, 
  Supplier, 
  StoreMetrics, 
  InventoryPrediction, 
  SalesInsight,
  User,
  StoreInfo,
  WorkStation,
  CookingMethodRequest,
  NewPurchaseOrder,
  NewPurchaseOrderStatus,
  CreatePurchaseOrderRequest,
  ProcessInboundRequest,
  CanOrderResult,
  OrderItemInfo,
  PackagingContainer,
  PackagingContainerTransaction,
  BulkUploadResponse
} from '../types';

// API URL 타입 정의
type ApiType = 'GATEWAY' | 'INVENTORY' | 'ORDER';

import { getApiUrl, config, getSafeApiUrl, baseUrlIncludesApi } from '../config/environment';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import { USE_MULTI_STORE_MOCK } from '../config/features';
import { safeApiCall } from '../utils/apiCaller';

/** GET /multi-store/promo 행 (집계 실패 시 effect* 생략·null) */
export type MultiStorePromoSummaryItem = {
  campaignId: number;
  storeId: number;
  storeName: string;
  promoName: string;
  effectRevenue?: number | null;
  effectOrders?: number | null;
  startAt?: string;
  endAt?: string;
  memo?: string | null;
  overlapWarning?: boolean;
  aggregationState?: 'ok' | 'failed';
  aggregationError?: string;
};

export type MultiStorePromoCampaignItem = {
  campaignId: number;
  storeId: number;
  name: string;
  startAt: string;
  endAt: string;
  memo?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

// 백엔드 API 주소 설정 - 중앙 환경 설정에서 가져옴
const getApiBaseUrl = (type: ApiType = 'GATEWAY') => {
  const url = getSafeApiUrl(type);
  
  // 로컬 스토리지에는 게이트웨이 URL만 저장 (하위 호환성)
  if (type === 'GATEWAY') {
    localStorage.setItem('apiUrl', url);
  }
  
  // 이미 /api가 포함된 URL인 경우 경고 없이 설정
  if (baseUrlIncludesApi(url)) {
    console.log(`API URL 설정 (${type}):`, url);
  } else {
    console.log(`API URL 설정 (${type}):`, url);
    console.log('- 현재 환경:', config.ENVIRONMENT);
    console.log('- 전체 API 설정:', config.API_URLS);
  }
  
  return url;
};

// 서비스 타입에 따른 axios 인스턴스 생성
const createApiInstance = (type: ApiType) => {
  const baseUrl = getApiBaseUrl(type);
  
  // getSafeApiUrl이 이미 /api를 포함하는 URL을 반환하므로 추가 처리 불필요
  // fixed double api path - getSafeApiUrl already handles /api inclusion
  const apiBaseUrl = baseUrl;
  
  // 이미 /api가 포함된 URL인 경우 간단한 로그만 출력
  if (baseUrlIncludesApi(baseUrl)) {
    console.log(`📡 ${type} API 인스턴스 생성:`, {
      finalBaseUrl: apiBaseUrl,
      hasApiInBase: true
    });
  } else {
    console.log(`📡 ${type} API 인스턴스 생성:`, {
      originalBaseUrl: baseUrl,
      finalBaseUrl: apiBaseUrl,
      hasApiInBase: false
    });
  }
  
  return axios.create({
    baseURL: apiBaseUrl,
    withCredentials: false,
    timeout: 30000, // 30초 타임아웃 설정
  });
};

// 기본 API 인스턴스 (게이트웨이)
export const api = createApiInstance('GATEWAY');

// 재고/메뉴 관리 API 인스턴스
export const inventoryApiInstance = createApiInstance('INVENTORY');

// 주문 관리 API 인스턴스
export const orderApi = createApiInstance('ORDER');

// 로컬 스토리지에서 API 설정 변경 감지
window.addEventListener('storage', (event) => {
  if (event.key === 'apiUrl' && event.newValue) {
    api.defaults.baseURL = event.newValue;
    console.log('게이트웨이 API 기본 URL이 변경되었습니다:', event.newValue);
  }
});

// API 로깅 설정
const shouldLogApiCalls = () => localStorage.getItem('apiLogging') !== 'false';

// 토큰 유효성 검증 함수
export const validateToken = (token: string): boolean => {
  // 로그 레벨이 debug인 경우에만 상세 로그 출력
  if (process.env.NODE_ENV === 'development') {
    console.log('✅ validateToken 호출됨');
  }
  
  try {
    // JWT 토큰 구조 확인 (header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) {
      if (process.env.NODE_ENV === 'development') {
        console.log('토큰 형식이 올바르지 않습니다.');
      }
      return false;
    }
    
    // 토큰 만료 시간 확인
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    if (payload.exp && payload.exp < currentTime) {
      if (process.env.NODE_ENV === 'development') {
        console.log('토큰이 만료되었습니다.');
      }
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('토큰 검증 중 오류:', error);
    return false;
  }
};



// API 요청 인터셉터 설정 함수
const setupInterceptors = (apiInstance: AxiosInstance) => {
  // 요청 인터셉터
  apiInstance.interceptors.request.use(
    (config) => {
      // 디버깅 정보 출력
      console.log('🔍 요청 URL:', config.url);
      console.log('🔍 baseURL:', config.baseURL);
      
      // URL 중복 /api 방지
      let finalUrl = config.url;
      if (config.baseURL && config.url) {
        if (config.baseURL.endsWith('/api') && config.url.startsWith('/api')) {
          // baseURL이 /api로 끝나고, url이 /api로 시작하면 중복 제거
          finalUrl = config.url.substring(4); // '/api' 제거
          console.log('🔍 중복 /api 제거됨:', finalUrl);
        }
      }
      
      // 최종 URL을 config.url에 직접 설정
      config.url = finalUrl;
      
      console.log('🔍 최종 URL:', finalUrl);
      console.log('🔍 config.url 설정됨:', config.url);
      
      const token = localStorage.getItem('token');
      console.log('🔍 현재 토큰:', token ? `${token.substring(0, 50)}...` : '없음');
      
      // 항상 로그인 관련 요청 로깅
      if (config.url?.includes('/login') || config.url?.includes('/register')) {
        console.log(`[AUTH DEBUG] 인증 요청: ${config.method?.toUpperCase()} ${config.url}`);
        console.log(`[AUTH DEBUG] 전체 URL: ${(config.baseURL || '')}${(config.url || '')}`);
        console.log(`[AUTH DEBUG] 요청 데이터:`, config.data);
      } else if (shouldLogApiCalls()) {
        console.log(`API 요청: ${config.method?.toUpperCase()} ${config.url}`, config.data);
      }
      
      if (token) {
        // 토큰 유효성 검증
        if (validateToken(token)) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('🔍 Authorization 헤더 설정됨');
          console.log(`토큰이 요청 헤더에 추가되었습니다. URL: ${config.url}`);
        } else {
          console.log('유효하지 않은 토큰 - 헤더에 추가하지 않습니다.');
          // 유효하지 않은 토큰 제거
          localStorage.removeItem('token');
        }
      } else {
        console.log(`토큰이 없습니다. URL: ${config.url}`);
      }
      
      // 매장 ID 헤더 및 쿼리 파라미터 처리 (인증 엔드포인트 제외)
      const storeId = localStorage.getItem('storeId');
      const isAuthEndpoint = !!config.url && (
        config.url.includes('/login') ||
        config.url.includes('/register') ||
        config.url.includes('/auth/login') ||
        config.url.includes('/users/register') ||
        // 현재 사용자 조회는 storeId 불필요
        config.url.includes('/users/current') ||
        config.url.includes('/stores/me') ||
        config.url.includes('/stores/auth') ||
        // 비밀번호 변경 엔드포인트는 storeId 헤더/쿼리 불필요
        /\/users\/\d+\/password(\b|\?|$)/.test(config.url) ||
        // 사용자 프로필 업데이트 엔드포인트도 storeId 불필요
        /\/users\/\d+(\b|\?|$)/.test(config.url) ||
        // 프로필 업데이트 엔드포인트 추가
        config.url.includes('/users/profile') ||
        config.url.includes('/users/update') ||
        // 작업관리 엔드포인트는 자체적으로 storeId 쿼리를 포함함
        config.url.includes('/work-management/workstations')
      );
      if (storeId && !isAuthEndpoint) {
        // 헤더에 매장 ID 추가
        config.headers['X-Store-Id'] = storeId;

              // URL에 storeId 쿼리 파라미터가 없는 경우에만 추가
      // 단, /stores/{id} 형태로 시작하는 URL은 이미 경로에 storeId가 포함되어 있으므로 제외
      if (finalUrl && !finalUrl.includes('storeId=') && !finalUrl.match(/\/stores\/\d+/)) {
        const separator = finalUrl.includes('?') ? '&' : '?';
        finalUrl = `${finalUrl}${separator}storeId=${storeId}`;
      }
      
      // 수정된 URL 적용
      config.url = finalUrl;
      }
      
      return config;
    },
    (error) => {
      console.error('API 요청 인터셉터 오류:', error);
      return Promise.reject(error);
    }
  );

  // 토큰 갱신 중인지 추적하는 플래그
  let isRefreshing = false;
  let failedQueue: Array<{ resolve: Function; reject: Function }> = [];

  const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  // 응답 인터셉터
  apiInstance.interceptors.response.use(
    (response) => {
      if (shouldLogApiCalls()) {
        console.log(`API 응답: ${response.config.method?.toUpperCase()} ${response.config.url}`, response.status);
      }
      
      // 사용자 정보 응답에서 isActive 확인 (강제 로그아웃 처리)
      if (response.data && (response.config.url?.includes('/stores/me') || response.config.url?.includes('/users/current'))) {
        if (response.data.isActive === false) {
          console.log('🚪 [API 응답] 비활성 계정 감지: 강제 로그아웃 처리');
          // AuthContext의 logout 함수 호출
          import('../contexts/AuthContext').then(module => {
            // AuthContext는 React Context이므로 직접 접근 불가
            // 대신 localStorage 정리 및 페이지 리로드
            localStorage.removeItem('token');
            localStorage.removeItem('storeId');
            localStorage.removeItem('currentUser');
            window.location.href = '/login';
          });
        }
      }
      
      // FCM 토큰 갱신 필요 여부 확인 (UNREGISTERED 자동 복구)
      if (response.data && response.data.needsTokenRefresh === true) {
        console.log('🔄 [FCM 자동 갱신] 백엔드가 토큰 갱신 요청 감지');
        
        // 중복 실행 방지: 이미 갱신 중이 아닐 때만 실행
        // pushNotificationService의 isAutoRefreshing 플래그로 자동 방지됨
        
        // 백그라운드에서 조용히 토큰 갱신 (사용자 모름, 비동기)
        import('./pushNotificationService').then(module => {
          const pushNotificationService = module.default;
          setTimeout(async () => {
            try {
              const refreshSuccess = await pushNotificationService.autoRefreshToken();
              if (refreshSuccess) {
                console.log('✅ [FCM 자동 갱신] UNREGISTERED 복구 완료');
              } else {
                console.warn('⚠️ [FCM 자동 갱신] 복구 실패 또는 이미 갱신 중');
              }
            } catch (error) {
              console.error('❌ [FCM 자동 갱신] 오류:', error);
            }
          }, 2000); // 2초 후 백그라운드에서 실행
        });
      }
      
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      
      // Mock 데이터 서비스 체크 (개발 모드에서만)
      const isDevMode = process.env.NODE_ENV === 'development';
      const enableMockUser = localStorage.getItem('enableMockUser') === 'true';
      
      if (isDevMode && enableMockUser) {
        // 네트워크 오류나 연결 거부 시 Mock 데이터 반환
        const isNetworkError = !error.response || error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED';
        
          if (isNetworkError && originalRequest.url) {
          const url = originalRequest.url;
          const { generateMockIngredients, generateMockSuppliers, generateMockPurchaseOrders, 
                  generateMockMainMenus, generateMockOptionMenus, generateMockOrders, 
                  generateMockStockTransactions, generateMockBatches,
                  generateMockCanOrderResult, generateMockOrderItems, generateMockPurchaseOrderDetail,
                  generateMockStoreOperationInfo } = await import('./mockDataService');
          
          // Mock 데이터 매핑
          let mockData: any = null;
          
          if (url.includes('/ingredients') && originalRequest.method === 'get') {
            mockData = generateMockIngredients();
            console.log('🛠️ [Mock Data] 식재료 Mock 데이터 반환:', mockData.length, '개');
          } else if (url.includes('/suppliers') && originalRequest.method === 'get' && !url.includes('/suppliers/')) {
            mockData = generateMockSuppliers();
            console.log('🛠️ [Mock Data] 거래처 Mock 데이터 반환:', mockData.length, '개');
          } else if (url.includes('/purchase-orders') && originalRequest.method === 'get') {
            // 발주서 상세 조회 (ID 포함)
            const purchaseOrderIdMatch = url.match(/\/purchase-orders\/(\d+)/);
            if (purchaseOrderIdMatch) {
              const orderId = parseInt(purchaseOrderIdMatch[1]);
              mockData = generateMockPurchaseOrderDetail(orderId);
              console.log('🛠️ [Mock Data] 발주서 상세 Mock 데이터 반환 (ID:', orderId, ')');
            } else if (!url.includes('/purchase-orders/')) {
              // 발주서 목록
              mockData = generateMockPurchaseOrders();
              console.log('🛠️ [Mock Data] 발주내역 Mock 데이터 반환:', mockData.length, '개');
            }
          } else if (url.includes('/can-order') && originalRequest.method === 'get') {
            // 발주 가능 여부 확인
            const supplierIdMatch = url.match(/\/supplier\/(\d+)\/can-order/);
            const supplierId = supplierIdMatch ? parseInt(supplierIdMatch[1]) : 1;
            mockData = generateMockCanOrderResult(supplierId);
            console.log('🛠️ [Mock Data] 발주 가능 여부 Mock 데이터 반환 (거래처 ID:', supplierId, ')');
          } else if (url.includes('/order-items') && originalRequest.method === 'get') {
            // 발주 가능한 항목 목록
            const supplierIdMatch = url.match(/\/supplier\/(\d+)\/order-items/);
            const supplierId = supplierIdMatch ? parseInt(supplierIdMatch[1]) : 1;
            mockData = generateMockOrderItems(supplierId);
            console.log('🛠️ [Mock Data] 발주 가능 항목 Mock 데이터 반환 (거래처 ID:', supplierId, ',', mockData.length, '개)');
          } else if (url.includes('/menus') && originalRequest.method === 'get') {
            if (url.includes('main') || (!url.includes('option') && !url.includes('/menus/'))) {
              mockData = generateMockMainMenus();
              console.log('🛠️ [Mock Data] 메인메뉴 Mock 데이터 반환:', mockData.length, '개');
            } else if (url.includes('option')) {
              mockData = generateMockOptionMenus();
              console.log('🛠️ [Mock Data] 옵션메뉴 Mock 데이터 반환:', mockData.length, '개');
            }
          } else if (url.includes('/orders') && originalRequest.method === 'get' && !url.includes('/orders/')) {
            mockData = generateMockOrders();
            console.log('🛠️ [Mock Data] 작업대(주문) Mock 데이터 반환:', mockData.length, '개');
          } else if (url.includes('/stock-transactions') && originalRequest.method === 'get') {
            mockData = generateMockStockTransactions();
            console.log('🛠️ [Mock Data] 입출기록 Mock 데이터 반환:', mockData.length, '개');
          } else if (url.includes('/batch-transactions') && originalRequest.method === 'get') {
            // URL에서 ingredientId 추출
            const ingredientIdMatch = url.match(/ingredientId=(\d+)/);
            const ingredientId = ingredientIdMatch ? parseInt(ingredientIdMatch[1]) : 1;
            mockData = generateMockBatches(ingredientId);
            console.log('🛠️ [Mock Data] 배치 Mock 데이터 반환:', mockData.length, '개');
          } else if (url.includes('/store-operation-info') && originalRequest.method === 'get') {
            // 운영 정보 조회
            const storeIdMatch = url.match(/\/store-operation-info\/(\d+)/);
            const storeId = storeIdMatch ? parseInt(storeIdMatch[1]) : 1;
            mockData = generateMockStoreOperationInfo(storeId);
            console.log('🛠️ [Mock Data] 운영 정보 Mock 데이터 반환 (매장 ID:', storeId, ')');
          }
          
          if (mockData !== null) {
            return Promise.resolve({
              data: mockData,
              status: 200,
              statusText: 'OK',
              headers: {},
              config: originalRequest,
            });
          }
        }
      }
      
      // 401 오류 (인증 만료) 처리 - 토큰 자동 갱신
      if (error.response && error.response.status === 401 && !originalRequest._retry) {
        const requestUrl = (originalRequest?.url || error.config?.url || '') as string;
        console.log('🔐 [401 오류] 인증 만료 감지:', requestUrl);
        
        // 선택적 엔드포인트(구독/결제/세팅 등): 401이어도 갱신·로그아웃 하지 않고 데이터 없음으로 처리 (대시보드 유지)
        const isOptionalEndpoint = /\/subscriptions\/user\/\d+/.test(requestUrl) ||
          /\/payment-methods\/user\/\d+/.test(requestUrl) ||
          /\/payments\/user\/\d+/.test(requestUrl) ||
          /\/installation-requests\/user\/\d+/.test(requestUrl);
        if (isOptionalEndpoint) {
          console.log('ℹ️ [401] 선택적 엔드포인트 - 데이터 없음으로 처리, 로그아웃 안 함');
          return Promise.resolve({
            data: null,
            status: 401,
            statusText: 'Optional endpoint 401',
            headers: error.response?.headers || {},
            config: originalRequest || error.config,
          });
        }
        
        // refresh-token 엔드포인트에서 401이 발생한 경우는 즉시 로그아웃
        if (originalRequest.url && originalRequest.url.includes('/refresh-token')) {
          console.log('❌ [토큰 갱신] refresh-token 실패 - 로그아웃 처리');
          
          // 알림 구독 정리
          try {
            const pushNotificationService = (await import('./pushNotificationService')).default;
            await pushNotificationService.unsubscribe();
            console.log('🔔 [로그아웃] 알림 구독 정리 완료');
          } catch (error) {
            console.warn('🔔 [로그아웃] 알림 구독 정리 실패:', error);
          }
          
          // 토큰 갱신 서비스 중지
          try {
            const tokenRefreshService = (await import('./tokenRefreshService')).default;
            tokenRefreshService.stopAutoRefresh();
          } catch (error) {
            console.warn('토큰 갱신 서비스 중지 실패:', error);
          }
          
          localStorage.removeItem('token');
          localStorage.removeItem('storeId');
          localStorage.removeItem('currentUser');
          
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
        
        // 토큰 갱신 시도
        if (isRefreshing) {
          // 이미 토큰 갱신 중이면 대기열에 추가
          console.log('⏳ [토큰 갱신] 이미 갱신 중 - 대기열 추가');
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers['Authorization'] = 'Bearer ' + token;
              return apiInstance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const currentToken = localStorage.getItem('token');
        
        if (!currentToken) {
          console.log('❌ [토큰 갱신] 토큰이 없음 - 로그아웃 처리');
          isRefreshing = false;
          processQueue(error, null);
          
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }

        try {
          console.log('🔄 [토큰 갱신] 자동 토큰 갱신 시도...');
          
          // 토큰 갱신은 앱과 동일한 GATEWAY(예: app.judam.kr/api) 사용 - CORS 및 호스트 일치
          const API_BASE_URL = getSafeApiUrl('GATEWAY');
          const response = await fetch(`${API_BASE_URL}/stores/refresh-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${currentToken}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            const newToken = data.token;
            
            // 새 토큰 저장
            localStorage.setItem('token', newToken);
            
            // 사용자 정보 업데이트
            if (data.user) {
              localStorage.setItem('currentUser', JSON.stringify(data.user));
            }
            
            console.log('✅ [토큰 갱신] 토큰 자동 갱신 성공 - 요청 재시도');
            
            // 대기 중인 요청들 처리
            processQueue(null, newToken);
            
            // 원래 요청 재시도
            originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
            isRefreshing = false;
            
            return apiInstance(originalRequest);
          } else {
            console.error('❌ [토큰 갱신] 토큰 갱신 실패:', response.status);
            throw new Error('Token refresh failed');
          }
        } catch (refreshError) {
          console.error('❌ [토큰 갱신] 토큰 갱신 중 오류:', refreshError);
          
          // 대기 중인 요청들에 오류 전달
          processQueue(refreshError, null);
          isRefreshing = false;
          
          // 알림 구독 정리
          try {
            const pushNotificationService = (await import('./pushNotificationService')).default;
            await pushNotificationService.unsubscribe();
            console.log('🔔 [로그아웃] 알림 구독 정리 완료');
          } catch (error) {
            console.warn('🔔 [로그아웃] 알림 구독 정리 실패:', error);
          }
          
          // 토큰 갱신 서비스 중지
          try {
            const tokenRefreshService = (await import('./tokenRefreshService')).default;
            tokenRefreshService.stopAutoRefresh();
          } catch (error) {
            console.warn('토큰 갱신 서비스 중지 실패:', error);
          }
          
          // 로그아웃 처리
          localStorage.removeItem('token');
          localStorage.removeItem('storeId');
          localStorage.removeItem('currentUser');
          
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          
          return Promise.reject(error);
        }
      }
      
      // 404 오류 처리 - 특정 엔드포인트는 데이터 없음을 의미하므로 조용히 처리
      if (error.response && error.response.status === 404) {
        // error.config.url 또는 originalRequest.url 사용 (쿼리 파라미터 포함)
        const url = error.config?.url || originalRequest?.url || '';
        
        // 데이터가 없을 수 있는 정상적인 엔드포인트들 (쿼리 파라미터 포함 가능)
        // 실제 사용되는 엔드포인트:
        // - /subscriptions/user/${userId}
        // - /payment-methods/user/${userId}
        // - /payments/user/${userId}
        // - /installation-requests/user/${userId}
        const isOptionalEndpoint = 
          /\/subscriptions\/user\/\d+/.test(url) ||
          /\/payment-methods\/user\/\d+/.test(url) ||
          /\/payments\/user\/\d+/.test(url) ||
          /\/installation-requests\/user\/\d+/.test(url);
        
        if (isOptionalEndpoint) {
          // 조용히 처리 (콘솔에 오류로 표시되지 않음)
          if (shouldLogApiCalls()) {
            console.log(`ℹ️ [API] 데이터 없음 (404): ${originalRequest?.method?.toUpperCase()} ${url}`);
          }
          // 404를 정상 응답으로 변환 (빈 데이터)
          return Promise.resolve({
            data: null,
            status: 404,
            statusText: 'Not Found',
            headers: error.response.headers,
            config: originalRequest || error.config,
          });
        }
      }
      
      return Promise.reject(error);
    }
  );
};

// 각 API 인스턴스에 인터셉터 설정
setupInterceptors(api);
setupInterceptors(inventoryApiInstance);
setupInterceptors(orderApi);

// 인증 API
const authApi = {
  login: async (email: string, password: string) => {
    try {
      console.log('API 요청 경로 확인:', api.defaults.baseURL + API_ENDPOINTS.AUTH.STORES_AUTH);
      const response = await safeApiCall.gateway.post(API_ENDPOINTS.AUTH.STORES_AUTH, { username: email, password });
      // 응답에서 토큰 추출 및 저장
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      const u = user as Record<string, unknown> | null | undefined;
      const storeId = u && (u.storeId !== undefined ? u.storeId : u.StoreId);
      const id = u && (u.id !== undefined ? u.id : u.Id);
      if (storeId != null) {
        localStorage.setItem('storeId', String(storeId));
      } else if (id != null) {
        localStorage.setItem('storeId', String(id));
      }

      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('storeId');
    window.location.href = '/login';
  },
  
  getProfile: async () => {
    return await safeApiCall.gateway.get<User>(API_ENDPOINTS.USERS.CURRENT);
  },

  register: async (userData: Omit<User, 'id'>) => {
    return await safeApiCall.gateway.post<User>(API_ENDPOINTS.USERS.REGISTER, userData);
  },

  changePassword: async (newPassword: string) => {
    const userId = (await authApi.getProfile()).data.id;
    return await safeApiCall.gateway.put(API_ENDPOINTS.USERS.UPDATE_PASSWORD(userId), { newPassword });
  },

  // 비밀번호 재설정 요청
  forgotPassword: async (email: string) => {
    return await safeApiCall.gateway.post('/auth/forgot-password', { email });
  },

  // 비밀번호 재설정 토큰 검증
  validateResetToken: async (token: string) => {
    return await safeApiCall.gateway.post('/stores/validate-reset-token', { token });
  },

  // 비밀번호 재설정
  resetPassword: async (token: string, newPassword: string) => {
    return await safeApiCall.gateway.post('/stores/reset-password', { token, newPassword });
  }
};

// 매장 API
const storeApi = {
  getStoreInfo: async (storeId: number) => {
    // 게이트웨이를 통한 호출이 실패하면 직접 API 호출 시도
    try {
      return await safeApiCall.gateway.get(API_ENDPOINTS.STORES.INFO(storeId));
    } catch (error) {
      console.log('게이트웨이를 통한 스토어 정보 조회 실패, 직접 API 호출 시도:', error);
      // 직접 API 호출 시도
      return await api.get(`/stores/${storeId}`);
    }
  },

  updateStoreInfo: async (storeId: number, storeData: Partial<StoreInfo>) => {
    // 게이트웨이를 통한 호출이 실패하면 직접 API 호출 시도
    try {
      return await safeApiCall.gateway.put(API_ENDPOINTS.STORES.INFO(storeId), storeData);
    } catch (error) {
      console.log('게이트웨이를 통한 스토어 업데이트 실패, 직접 API 호출 시도:', error);
      // 직접 API 호출 시도
      return await api.put(`/stores/${storeId}`, storeData);
    }
  },

  getStoreSettings: async (storeId: number) => {
    return await api.get<any>(`/stores/${storeId}/settings`);
  },

  updateStoreSettings: async (storeId: number, settings: any) => {
    return await api.put<any>(`/stores/${storeId}/settings`, settings);
  },

  /** 다중 매장: 현재 사용자 접근 가능 매장 목록. 응답: { stores: [{ storeId, storeName }], canAddStore, storeCount } */
  getMyStores: async () => {
    return await safeApiCall.gateway.get<{ stores: { storeId: number; storeName: string }[]; canAddStore: boolean; storeCount: number }>(API_ENDPOINTS.STORES.ME_STORES);
  },

  /** 다중 매장: 추가 매장 생성(사업자 정보). */
  createStore: async (body: { name: string; address?: string; phoneNumber?: string; ownerName?: string; businessRegistrationNumber?: string; businessLicenseUrl?: string }) => {
    return await safeApiCall.gateway.post<StoreInfo>(API_ENDPOINTS.STORES.ME_STORES, body);
  }
};

/** 다중매장 월 목표 목업 상태(게이트웨이 실패·USE_MULTI_STORE_MOCK 시 saveGoal/getGoals 일관) */
let multiStoreMockGoalsState: { storeId: number; period: string; goalAmount: number }[] = [
  { storeId: 1, period: '2026-03', goalAmount: 8_000_000 },
  { storeId: 2, period: '2026-03', goalAmount: 6_000_000 },
];

/** 목업: 프로모 캠페인 메타(USE_MULTI_STORE_MOCK 시 CRUD·getPromo 일관) */
let multiStoreMockPromoCampaigns: { id: number; storeId: number; name: string; startAt: string; endAt: string; memo: string | null }[] = [
  { id: 101, storeId: 1, name: '세트 할인', startAt: '2026-03-01T00:00:00.000Z', endAt: '2026-03-31T00:00:00.000Z', memo: null },
  { id: 103, storeId: 1, name: '봄 시즌', startAt: '2026-03-15T00:00:00.000Z', endAt: '2026-04-10T00:00:00.000Z', memo: null },
  { id: 102, storeId: 2, name: '런치 이벤트', startAt: '2026-03-10T00:00:00.000Z', endAt: '2026-03-20T00:00:00.000Z', memo: '목업' },
];

function multiStoreMockPromoIntervalsOverlap(aS: string, aE: string, bS: string, bE: string): boolean {
  const s1 = new Date(aS).getTime();
  const e1 = new Date(aE).getTime();
  const s2 = new Date(bS).getTime();
  const e2 = new Date(bE).getTime();
  if ([s1, e1, s2, e2].some((t) => Number.isNaN(t))) return false;
  return s1 < e2 && s2 < e1;
}

function multiStoreMockPromoOverlapIds(
  campaigns: { id: number; storeId: number; startAt: string; endAt: string }[]
): Set<number> {
  const warned = new Set<number>();
  const byStore: Record<number, { id: number; storeId: number; startAt: string; endAt: string }[]> = {};
  for (let i = 0; i < campaigns.length; i++) {
    const c = campaigns[i];
    const sid = c.storeId;
    if (!byStore[sid]) byStore[sid] = [];
    byStore[sid].push(c);
  }
  const storeIds = Object.keys(byStore);
  for (let s = 0; s < storeIds.length; s++) {
    const list = byStore[Number(storeIds[s])];
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i];
        const b = list[j];
        if (multiStoreMockPromoIntervalsOverlap(a.startAt, a.endAt, b.startAt, b.endAt)) {
          warned.add(a.id);
          warned.add(b.id);
        }
      }
    }
  }
  return warned;
}

// 다중 매장 집계·인사이트 API (Phase 1.2~1.9). 백엔드 미준비 시 목업 반환.
const MULTI_STORE_MOCK = {
  summary: (params: { startDate: string; endDate: string; storeCount?: number }) => ({
    totalRevenue: 0,
    orderCount: 0,
    avgCostRate: 0,
    storeCount: params.storeCount ?? 0,
    updatedAt: new Date().toISOString(),
  }),
  attentionStores: () => ({ items: [] as { storeId: number; storeName: string; reason: string }[] }),
  performance: (params?: { page?: number; limit?: number; sortBy?: string }) => {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 10;
    const total = 18;
    const all = Array.from({ length: total }, (_, i) => ({
      storeId: i + 1,
      storeName: `매장 ${i + 1}`,
      sales: (i + 1) * 500000,
      orderCount: (i + 1) * 120,
      costRate: 28 + (i % 5),
    }));
    const start = (page - 1) * limit;
    const items = all.slice(start, start + limit);
    return { items, total };
  },
  storeHealth: () => ({ health: 'normal' as const, factors: [] as { label: string; value: string }[] }),
  recommendedActions: () => ({ actions: [] as { text: string; priority: number }[] }),
  goalVsActual: () => ({
    items: [
      { storeId: 1, storeName: '매장 A', goal: 8000000, actual: 7200000, achievementRate: 90, gap: -800000, hasConfiguredGoal: true },
      { storeId: 2, storeName: '매장 B', goal: 6000000, actual: 5300000, achievementRate: 88.3, gap: -700000, hasConfiguredGoal: true },
    ] as { storeId: number; storeName: string; goal: number; actual: number; achievementRate: number; gap: number; hasConfiguredGoal?: boolean }[],
  }),
  goals: () => ({
    goals: multiStoreMockGoalsState.map((g) => ({ ...g })),
  }),
  trends: () => ({
    series: [
      { storeId: 1, storeName: '매장 A', data: [{ label: '1일', value: 950000 }, { label: '2일', value: 1020000 }, { label: '3일', value: 1100000 }, { label: '4일', value: 980000 }, { label: '5일', value: 1150000 }, { label: '6일', value: 1000000 }, { label: '7일', value: 1000000 }] },
      { storeId: 2, storeName: '매장 B', data: [{ label: '1일', value: 720000 }, { label: '2일', value: 780000 }, { label: '3일', value: 750000 }, { label: '4일', value: 800000 }, { label: '5일', value: 760000 }, { label: '6일', value: 790000 }, { label: '7일', value: 810000 }] },
    ] as { storeId: number; storeName: string; data: { label: string; value: number }[] }[],
  }),
  costMargin: () => ({
    items: [
      { storeId: 1, storeName: '매장 A', costRate: 30.2, marginRate: 69.8, status: '양호' as const },
      { storeId: 2, storeName: '매장 B', costRate: 34.5, marginRate: 65.5, status: '주의' as const },
    ] as { storeId: number; storeName: string; costRate: number; marginRate: number; status: '위험' | '경계' | '양호' }[],
  }),
  benchmark: () => ({
    topStores: [{ storeId: 1, storeName: '매장 A', score: 9200000 }] as { storeId: number; storeName: string; score: number }[],
    patterns: [
      { title: '원가 관리', description: '상위 매장은 원가율 30% 이하 유지' },
      { title: '피크 타임 대응', description: '점심 12–14시 재고·인력 사전 점검' },
    ] as { title: string; description: string }[],
  }),
  promo: (params: { page?: number; pageSize?: number }) => {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 50;
    const storeNames: Record<number, string> = { 1: '매장 A', 2: '매장 B' };
    const overlapIds = multiStoreMockPromoOverlapIds(multiStoreMockPromoCampaigns);
    const all = multiStoreMockPromoCampaigns.map((c) => ({
      campaignId: c.id,
      storeId: c.storeId,
      storeName: storeNames[c.storeId] ?? `매장 ${c.storeId}`,
      promoName: c.name,
      effectRevenue: 450000 - c.id,
      effectOrders: 28,
      startAt: c.startAt,
      endAt: c.endAt,
      memo: c.memo,
      overlapWarning: overlapIds.has(c.id),
      aggregationState: 'ok' as const,
    }));
    const totalCount = all.length;
    const start = (page - 1) * pageSize;
    return { items: all.slice(start, start + pageSize), totalCount };
  },
  franchiseUsage: () => ({
    items: [
      { storeId: 2, storeName: '매장 B', lastViewedAt: '2026-03-10', dataUsageLevel: 'low' as const },
    ] as { storeId: number; storeName: string; lastViewedAt?: string; dataUsageLevel: 'high' | 'medium' | 'low' }[],
  }),
};

const multiStoreApi = {
  /** 1.2 요약 집계. 기간·매장 기준 KPI, 갱신 시점. */
  getSummary: async (params: { startDate: string; endDate: string; storeIds?: number[] }) => {
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
      if (params.storeIds?.length) q.set('storeIds', params.storeIds.join(','));
      const res = await safeApiCall.gateway.get<{
        totalRevenue: number; orderCount: number; avgCostRate: number; storeCount: number; updatedAt: string;
      }>(`${API_ENDPOINTS.MULTI_STORE.SUMMARY}?${q}`);
      return res?.data ?? MULTI_STORE_MOCK.summary({ ...params, storeCount: (res?.data as any)?.storeCount ?? 0 });
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return MULTI_STORE_MOCK.summary({ ...params, storeCount: 0 });
    }
  },
  /** 1.3 주의/위험 매장. 상한 20개. storeIds 선택 시 해당 매장만. */
  getAttentionStores: async (params: { startDate: string; endDate: string; storeIds?: number[] }) => {
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
      if (params.storeIds?.length) q.set('storeIds', params.storeIds.join(','));
      const res = await safeApiCall.gateway.get<{ items: { storeId: number; storeName: string; reason: string }[] }>(
        `${API_ENDPOINTS.MULTI_STORE.ATTENTION_STORES}?${q}`
      );
      return res?.data ?? MULTI_STORE_MOCK.attentionStores();
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return MULTI_STORE_MOCK.attentionStores();
    }
  },
  /** 1.5 매장별 성과 요약. 페이징·정렬. */
  getPerformance: async (params: { startDate: string; endDate: string; page?: number; limit?: number; sortBy?: string }) => {
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
      if (params.page != null) q.set('page', String(params.page));
      if (params.limit != null) q.set('limit', String(params.limit));
      if (params.sortBy) q.set('sortBy', params.sortBy);
      const res = await safeApiCall.gateway.get<{
        items: { storeId: number; storeName: string; sales: number; orderCount: number; costRate: number }[];
        total?: number;
        totalCount?: number;
      }>(`${API_ENDPOINTS.MULTI_STORE.PERFORMANCE}?${q}`);
      const raw = res?.data;
      if (raw && Array.isArray(raw.items)) {
        const t = raw.total ?? raw.totalCount;
        const n = typeof t === 'number' ? t : t != null && String(t).trim() !== '' ? Number(t) : NaN;
        const total = !Number.isNaN(n) ? Math.max(0, Math.trunc(n)) : raw.items.length;
        return { items: raw.items, total };
      }
      return MULTI_STORE_MOCK.performance({ page: params.page, limit: params.limit, sortBy: params.sortBy });
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return MULTI_STORE_MOCK.performance({ page: params.page, limit: params.limit, sortBy: params.sortBy });
    }
  },
  /** 1.6 운영 건강도·변동 요인. 매장 1개. */
  getStoreHealth: async (storeId: number, params: { startDate: string; endDate: string }) => {
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
      const res = await safeApiCall.gateway.get<{ health: string; factors: { label: string; value: string }[] }>(
        `${API_ENDPOINTS.MULTI_STORE.STORE_HEALTH(storeId)}?${q}`
      );
      return res?.data ?? MULTI_STORE_MOCK.storeHealth();
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return MULTI_STORE_MOCK.storeHealth();
    }
  },
  /** 1.9 추천 액션 1~3건. storeIds 선택 시 해당 매장만. */
  getRecommendedActions: async (params: { startDate: string; endDate: string; storeIds?: number[] }) => {
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
      if (params.storeIds?.length) q.set('storeIds', params.storeIds.join(','));
      const res = await safeApiCall.gateway.get<{ actions: { text: string; priority: number }[] }>(
        `${API_ENDPOINTS.MULTI_STORE.RECOMMENDED_ACTIONS}?${q}`
      );
      return res?.data ?? MULTI_STORE_MOCK.recommendedActions();
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return MULTI_STORE_MOCK.recommendedActions();
    }
  },
  /** 4.1 목표 vs 실적 */
  getGoalVsActual: async (params: { startDate: string; endDate: string; storeIds?: number[] }) => {
    const filterMockItems = (
      items: {
        storeId: number;
        storeName: string;
        goal: number;
        actual: number;
        achievementRate: number;
        gap: number;
        hasConfiguredGoal?: boolean;
      }[]
    ) => {
      if (!params.storeIds?.length) return items;
      const set = new Set(params.storeIds);
      return items.filter((x) => set.has(x.storeId));
    };
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
      if (params.storeIds?.length) q.set('storeIds', params.storeIds.join(','));
      const res = await safeApiCall.gateway.get<{
        items: {
          storeId: number;
          storeName: string;
          goal: number;
          actual: number;
          achievementRate: number;
          gap: number;
          hasConfiguredGoal?: boolean;
        }[];
      }>(`${API_ENDPOINTS.MULTI_STORE.GOAL_VS_ACTUAL}?${q}`);
      if (res?.data) return res.data;
      return { items: filterMockItems(MULTI_STORE_MOCK.goalVsActual().items) };
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return { items: filterMockItems(MULTI_STORE_MOCK.goalVsActual().items) };
    }
  },
  /** 저장된 월 목표 목록 (GET /multi-store/goals) */
  getGoals: async (): Promise<{ goals: { storeId: number; period: string; goalAmount: number }[] }> => {
    try {
      const res = await safeApiCall.gateway.get<{ goals: { storeId: number; period: string; goalAmount: number }[] }>(
        API_ENDPOINTS.MULTI_STORE.GOALS
      );
      return res?.data ?? { goals: MULTI_STORE_MOCK.goals().goals };
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return { goals: multiStoreMockGoalsState.map((g) => ({ ...g })) };
    }
  },
  /** 월 목표 저장·갱신 (POST /multi-store/goals) */
  saveGoal: async (body: { storeId: number; period: string; goalAmount: number }): Promise<void> => {
    try {
      await safeApiCall.gateway.post(API_ENDPOINTS.MULTI_STORE.GOALS, body);
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      const idx = multiStoreMockGoalsState.findIndex((x) => x.storeId === body.storeId && x.period === body.period);
      if (idx >= 0) multiStoreMockGoalsState[idx] = { ...body };
      else multiStoreMockGoalsState.push({ ...body });
    }
  },
  /** 월 목표 삭제 (DELETE /multi-store/goals?storeId&period) */
  deleteGoal: async (params: { storeId: number; period: string }): Promise<void> => {
    const q = new URLSearchParams({ storeId: String(params.storeId), period: params.period });
    try {
      await safeApiCall.gateway.delete(`${API_ENDPOINTS.MULTI_STORE.GOALS}?${q}`);
    } catch (e: unknown) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      const ax = e as { response?: { status?: number } };
      if (ax.response != null) throw e;
      const idx = multiStoreMockGoalsState.findIndex((x) => x.storeId === params.storeId && x.period === params.period);
      if (idx >= 0) multiStoreMockGoalsState.splice(idx, 1);
    }
  },
  /** 4.2 매출 트렌드 (일별/시간대/요일) */
  getTrends: async (params: { startDate: string; endDate: string; storeIds?: number[]; groupBy?: 'day' | 'hour' | 'weekday' }) => {
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
      if (params.storeIds?.length) q.set('storeIds', params.storeIds.join(','));
      if (params.groupBy) q.set('groupBy', params.groupBy);
      const res = await safeApiCall.gateway.get<{ series: { storeId: number; storeName: string; data: { label: string; value: number }[] }[] }>(
        `${API_ENDPOINTS.MULTI_STORE.TRENDS}?${q}`
      );
      return res?.data ?? MULTI_STORE_MOCK.trends();
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return MULTI_STORE_MOCK.trends();
    }
  },
  /** 4.3 원가·마진 */
  getCostMargin: async (params: { startDate: string; endDate: string }) => {
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
      const res = await safeApiCall.gateway.get<{
        items: {
          storeId: number;
          storeName: string;
          costRate: number;
          marginRate: number;
          netProfit?: number;
          netProfitRate?: number;
          status: '위험' | '경계' | '양호';
          industryGroup?: string;
          thresholdSource?: string;
          thresholdVersion?: string;
          thresholdBasis?: string;
          thresholdText?: string;
        }[];
      }>(
        `${API_ENDPOINTS.MULTI_STORE.COST_MARGIN}?${q}`
      );
      return res?.data ?? MULTI_STORE_MOCK.costMargin();
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return MULTI_STORE_MOCK.costMargin();
    }
  },
  /** 4.4 벤치마크 */
  getBenchmark: async (params: { startDate: string; endDate: string; topN?: number }) => {
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
      if (params.topN != null) q.set('topN', String(params.topN));
      const res = await safeApiCall.gateway.get<{ topStores: { storeId: number; storeName: string; score: number }[]; patterns: { title: string; description: string }[] }>(
        `${API_ENDPOINTS.MULTI_STORE.BENCHMARK}?${q}`
      );
      return res?.data ?? MULTI_STORE_MOCK.benchmark();
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return MULTI_STORE_MOCK.benchmark();
    }
  },
  /** 4.5 프로모션 효과(캠페인별 주문 집계). 목록 필터 startDate/endDate·페이징·totalCount */
  getPromo: async (params: { startDate: string; endDate: string; storeIds?: number[]; page?: number; pageSize?: number }) => {
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
      if (params.storeIds?.length) q.set('storeIds', params.storeIds.join(','));
      if (params.page != null) q.set('page', String(params.page));
      if (params.pageSize != null) q.set('pageSize', String(params.pageSize));
      const res = await safeApiCall.gateway.get<{
        items: MultiStorePromoSummaryItem[];
        totalCount: number;
      }>(`${API_ENDPOINTS.MULTI_STORE.PROMO}?${q}`);
      return res?.data ?? MULTI_STORE_MOCK.promo({ page: params.page, pageSize: params.pageSize });
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return MULTI_STORE_MOCK.promo({ page: params.page, pageSize: params.pageSize });
    }
  },
  /** 프로모 캠페인 목록(등록 목록용, 기간 미지정 시 전체) */
  listPromoCampaigns: async (params?: { startDate?: string; endDate?: string; storeIds?: number[] }) => {
    try {
      const q = new URLSearchParams();
      if (params?.startDate) q.set('startDate', params.startDate);
      if (params?.endDate) q.set('endDate', params.endDate);
      if (params?.storeIds?.length) q.set('storeIds', params.storeIds.join(','));
      const qs = q.toString();
      const res = await safeApiCall.gateway.get<{ items: MultiStorePromoCampaignItem[] }>(
        qs ? `${API_ENDPOINTS.MULTI_STORE.PROMO_CAMPAIGNS}?${qs}` : API_ENDPOINTS.MULTI_STORE.PROMO_CAMPAIGNS
      );
      return res?.data ?? { items: [] };
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      const items = multiStoreMockPromoCampaigns.map((c) => ({
        campaignId: c.id,
        storeId: c.storeId,
        name: c.name,
        startAt: c.startAt,
        endAt: c.endAt,
        memo: c.memo,
      }));
      return { items };
    }
  },
  /** 프로모 캠페인 생성 */
  createPromoCampaign: async (body: { storeId: number; name: string; startAt: string; endAt: string; memo?: string | null }) => {
    try {
      const res = await safeApiCall.gateway.post<{
        campaignId: number;
        storeId: number;
        name: string;
        startAt: string;
        endAt: string;
        memo?: string | null;
      }>(API_ENDPOINTS.MULTI_STORE.PROMO_CAMPAIGNS, body);
      return res?.data;
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      const id = Date.now();
      multiStoreMockPromoCampaigns.push({
        id,
        storeId: body.storeId,
        name: body.name,
        startAt: body.startAt,
        endAt: body.endAt,
        memo: body.memo ?? null,
      });
      return { campaignId: id, storeId: body.storeId, name: body.name, startAt: body.startAt, endAt: body.endAt, memo: body.memo ?? null };
    }
  },
  /** 프로모 캠페인 수정 */
  updatePromoCampaign: async (
    campaignId: number,
    body: Partial<{ storeId: number; name: string; startAt: string; endAt: string; memo: string | null }>
  ) => {
    try {
      const res = await safeApiCall.gateway.patch<{
        campaignId: number;
        storeId: number;
        name: string;
        startAt: string;
        endAt: string;
        memo?: string | null;
      }>(`${API_ENDPOINTS.MULTI_STORE.PROMO_CAMPAIGNS}/${campaignId}`, body);
      return res?.data;
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      const idx = multiStoreMockPromoCampaigns.findIndex((x) => x.id === campaignId);
      if (idx < 0) throw e;
      multiStoreMockPromoCampaigns[idx] = { ...multiStoreMockPromoCampaigns[idx], ...body, memo: body.memo !== undefined ? body.memo : multiStoreMockPromoCampaigns[idx].memo };
      const c = multiStoreMockPromoCampaigns[idx];
      return { campaignId: c.id, storeId: c.storeId, name: c.name, startAt: c.startAt, endAt: c.endAt, memo: c.memo };
    }
  },
  /** 프로모 캠페인 삭제 */
  deletePromoCampaign: async (campaignId: number) => {
    try {
      await safeApiCall.gateway.delete(`${API_ENDPOINTS.MULTI_STORE.PROMO_CAMPAIGNS}/${campaignId}`);
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      multiStoreMockPromoCampaigns = multiStoreMockPromoCampaigns.filter((x) => x.id !== campaignId);
    }
  },
  /** 4.6 가맹점 활용 현황 */
  getFranchiseUsage: async () => {
    try {
      const res = await safeApiCall.gateway.get<{ items: { storeId: number; storeName: string; lastViewedAt?: string; dataUsageLevel: string }[] }>(
        API_ENDPOINTS.MULTI_STORE.FRANCHISE_USAGE
      );
      return res?.data ?? MULTI_STORE_MOCK.franchiseUsage();
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return MULTI_STORE_MOCK.franchiseUsage();
    }
  },
  /** 가맹점 활용 조회 이벤트 기록 */
  markFranchiseUsageView: async (storeId: number) => {
    try {
      await safeApiCall.gateway.post(`${API_ENDPOINTS.MULTI_STORE.FRANCHISE_USAGE}/view`, { storeId });
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
    }
  },
  /** 4.7 리포트 다운로드 (csv/xlsx) */
  downloadReport: async (params: { startDate: string; endDate: string; storeIds?: number[]; format?: 'csv' | 'xlsx' }) => {
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate, format: params.format || 'csv' });
      if (params.storeIds?.length) q.set('storeIds', params.storeIds.join(','));
      await safeApiCall.gateway.get(`${API_ENDPOINTS.MULTI_STORE.REPORT_DOWNLOAD}?${q}`, { responseType: 'blob' });
      return { ok: true };
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return { ok: false };
    }
  },
  /** 4.8 KPI 익스포트 (mock) */
  exportKpi: async (params: { startDate: string; endDate: string; storeIds?: number[]; format?: 'csv' | 'json' }) => {
    try {
      const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate, format: params.format || 'csv' });
      if (params.storeIds?.length) q.set('storeIds', params.storeIds.join(','));
      await safeApiCall.gateway.get(`${API_ENDPOINTS.MULTI_STORE.EXPORT}?${q}`, { responseType: 'blob' });
      return { ok: true };
    } catch (e) {
      if (!USE_MULTI_STORE_MOCK) throw e;
      return { ok: false };
    }
  },
};

// 주문 API
const ordersApi = {
  getAllOrders: async (storeId: number, params?: { 
    status?: OrderStatus, 
    page?: number, 
    limit?: number,
    startDate?: string,
    endDate?: string,
    searchTerm?: string
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('storeId', storeId.toString());
    
    if (params) {
      if (params.status !== undefined) queryParams.append('status', params.status.toString());
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.searchTerm) queryParams.append('search', params.searchTerm);
    }
    
    return await orderApi.get<{orders: Order[], totalCount: number}>(`/orders?${queryParams.toString()}`);
  },
  
  getOrderById: async (id: number) => {
    return await orderApi.get<Order>(`/orders/${id}`);
  },
  
  updateOrderStatus: async (id: number, status: OrderStatus) => {
    return await orderApi.put<Order>(`/orders/${id}/status`, { status });
  },

  addOrderNote: async (id: number, note: string) => {
    return await orderApi.post<Order>(`/orders/${id}/notes`, { note });
  },

  getOrderStatistics: async (storeId: number, startDate: string, endDate: string) => {
    return await orderApi.get<any>(`/orders/statistics?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}`);
  },

  exportOrders: async (storeId: number, startDate: string, endDate: string, format: 'csv' | 'excel') => {
    return await orderApi.get<Blob>(
      `/orders/export?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}&format=${format}`,
      { responseType: 'blob' }
    );
  },

  // 주문 목록 조회 API - 브릿지앱 형식으로 응답 반환
  getOrders: async (storeId: number, platform?: string, fromDate?: string, toDate?: string) => {
    try {
      // 쿼리 파라미터 생성
      const queryParams = new URLSearchParams();
      queryParams.append('storeId', storeId.toString());
      
      if (platform) queryParams.append('platform', platform);
      if (fromDate) queryParams.append('startDate', fromDate);
      if (toDate) queryParams.append('endDate', toDate);
      
      // API 요청 URL 로깅
      const url = `/orders?${queryParams.toString()}`;
      console.log(`주문 API 요청 URL: ${api.defaults.baseURL}${url}`);
      console.log(`매장 ID: ${storeId}, 로컬 스토리지 storeId: ${localStorage.getItem('storeId')}`);
      console.log(`날짜 필터: startDate=${fromDate}, endDate=${toDate}`);
      
      // API 호출을 Gateway API로 변경
      const response = await api.get(`${url.startsWith('/') ? url : '/' + url}`);
      console.log('API 주문 응답 상태:', response.status);
      console.log('API 주문 응답 데이터:', response.data);
      return response;
    } catch (error) {
      console.error('주문 데이터 조회 오류:', error);
      console.error('요청 정보:', {
        storeId,
        baseURL: api.defaults.baseURL,
        headers: api.defaults.headers
      });
      throw error;
    }
  },
};

// 재고 API
export const inventoryApi = {
  getAllIngredients: async (storeId: number) => {
    return await inventoryApiInstance.get<Ingredient[]>(`/inventory/ingredients?storeId=${storeId}`);
  },
  
  getIngredientById: async (id: number) => {
    return await inventoryApiInstance.get<Ingredient>(`/inventory/ingredients/${id}`);
  },
  
  createIngredient: async (ingredient: Omit<Ingredient, 'id'>) => {
    try {
      // storeId가 없는 경우 로컬 스토리지에서 가져옴
      if (!ingredient.storeId) {
        const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
        ingredient = { ...ingredient, storeId };
      }
      
      // 프론트엔드의 volume 필드를 백엔드의 Quantity 필드로 매핑
      const backendIngredient = {
        ...ingredient,
        Quantity: ingredient.volume, // volume을 Quantity로 매핑
        Unit: ingredient.unit, // unit을 Unit으로 매핑
        CurrentQuantity: ingredient.currentQuantity, // 사용자 입력 현 수량 그대로 사용
        CurrentQuantityUnit: ingredient.currentQuantityUnit,
        CurrentVolume: ingredient.volume * ingredient.currentQuantity, // 현 용량 계산
        VolumeUnit: ingredient.unit, // volumeUnit을 unit과 동일하게 설정
        OrderThreshold: ingredient.orderThreshold,
        InboundRequestQuantity: ingredient.inboundRequestQuantity,
        SupplierId: ingredient.supplierId,
        SupplierName: ingredient.supplierName,
        StoreId: ingredient.storeId,
        ExpiryDate: ingredient.expiryDate,
        SkipExpiryDate: ingredient.skipExpiryDate,
        // VAT 관련 필드
        VatApplicable: ingredient.vatApplicable,
        UnitPriceSource: ingredient.unitPriceSource,
        VatRate: ingredient.vatRate
      };
      
      // API 요청 URL 로깅
      const url = '/inventory/ingredients';
      console.log(`식재료 생성 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('원본 식재료 데이터:', ingredient);
      console.log('식재료 생성 요청 데이터 (백엔드 매핑 후):', backendIngredient);
      
      // API 호출
      const response = await inventoryApiInstance.post<Ingredient>(url, backendIngredient);
      console.log('API 식재료 생성 응답 상태:', response.status);
      console.log('API 식재료 생성 응답 데이터:', response.data);
      return response;
    } catch (error) {
      console.error('식재료 생성 오류:', error);
      throw error;
    }
  },

  updateIngredient: async (id: number, ingredient: Partial<Ingredient>) => {
    try {
      // storeId가 없는 경우 로컬 스토리지에서 가져옴
      if (!ingredient.storeId) {
        const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
        ingredient = { ...ingredient, storeId };
      }
      
      // 프론트엔드의 volume 필드를 백엔드의 Quantity 필드로 매핑
      const backendIngredient: any = { ...ingredient };
      if (ingredient.volume !== undefined) {
        backendIngredient.Quantity = ingredient.volume;
      }
      if (ingredient.unit !== undefined) {
        backendIngredient.Unit = ingredient.unit;
      }
      if (ingredient.currentQuantity !== undefined) {
        backendIngredient.CurrentQuantity = ingredient.currentQuantity;
      }
      if (ingredient.currentQuantityUnit !== undefined) {
        backendIngredient.CurrentQuantityUnit = ingredient.currentQuantityUnit;
      }
      if (ingredient.currentVolume !== undefined) {
        backendIngredient.CurrentVolume = ingredient.currentVolume;
      }
      if (ingredient.volumeUnit !== undefined) {
        backendIngredient.VolumeUnit = ingredient.volumeUnit;
      }
      if (ingredient.orderThreshold !== undefined) {
        backendIngredient.OrderThreshold = ingredient.orderThreshold;
      }
      if (ingredient.inboundRequestQuantity !== undefined) {
        backendIngredient.InboundRequestQuantity = ingredient.inboundRequestQuantity;
      }
      if (ingredient.supplierId !== undefined) {
        backendIngredient.SupplierId = ingredient.supplierId;
      }
      if (ingredient.supplierName !== undefined) {
        backendIngredient.SupplierName = ingredient.supplierName;
      }
      if (ingredient.storeId !== undefined) {
        backendIngredient.StoreId = ingredient.storeId;
      }
      if (ingredient.expiryDate !== undefined) {
        backendIngredient.ExpiryDate = ingredient.expiryDate;
      }
      if (ingredient.skipExpiryDate !== undefined) {
        backendIngredient.SkipExpiryDate = ingredient.skipExpiryDate;
      }
      // VAT 관련 필드
      if (ingredient.vatApplicable !== undefined) {
        backendIngredient.VatApplicable = ingredient.vatApplicable;
      }
      if (ingredient.unitPriceSource !== undefined) {
        backendIngredient.UnitPriceSource = ingredient.unitPriceSource;
      }
      if (ingredient.vatRate !== undefined) {
        backendIngredient.VatRate = ingredient.vatRate;
      }
      
      // API 요청 URL 로깅
      const url = `/inventory/ingredients/${id}`;
      console.log(`식재료 업데이트 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('식재료 업데이트 요청 데이터 (백엔드 매핑 후):', backendIngredient);
      
      // API 호출
      const response = await inventoryApiInstance.put<Ingredient>(url, backendIngredient);
      console.log('API 식재료 업데이트 응답 상태:', response.status);
      console.log('API 식재료 업데이트 응답 데이터:', response.data);
      return response;
    } catch (error) {
      console.error(`식재료 ID ${id} 업데이트 오류:`, error);
      throw error;
    }
  },

  deleteIngredient: async (id: number) => {
    try {
      // 스토어 ID 가져오기
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      
      // API 요청 URL 로깅
      const url = `/inventory/ingredients/${id}?storeId=${storeId}`;
      console.log(`식재료 삭제 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      // API 호출
      const response = await inventoryApiInstance.delete(url);
      console.log('API 식재료 삭제 응답 상태:', response.status);
      return response;
    } catch (error: any) {
      console.error(`식재료 ID ${id} 삭제 오류:`, error);
      
      // 오류 응답 상세 로깅
      if (error.response) {
        console.error('오류 상태 코드:', error.response.status);
        console.error('오류 응답 데이터:', error.response.data);
      }
      
      throw error;
    }
  },
  
  getLowStockIngredients: async (storeId: number) => {
    return await inventoryApiInstance.get<Ingredient[]>(`/inventory/ingredients/low-stock?storeId=${storeId}`);
  },
  
  deductIngredients: async (ingredientsData: { [key: number]: number }) => {
    return await inventoryApiInstance.post<any>('/inventory/ingredients/deduct', {
      ingredients: Object.entries(ingredientsData).map(([id, quantity]) => ({
        id: parseInt(id),
        quantity
      }))
    });
  },
  
  getAutoOrderSettings: async (storeId: number) => {
    return await inventoryApiInstance.get<any>(`/inventory/auto-order/settings?storeId=${storeId}`);
  },
  
  updateAutoOrderSettings: async (storeId: number, settings: { 
    enabled: boolean, 
    notificationDelay: number 
  }) => {
    return await inventoryApiInstance.put<any>(`/inventory/auto-order/settings?storeId=${storeId}`, settings);
  },
  
  getPendingOrderItems: async (storeId: number) => {
    return await inventoryApiInstance.get<any[]>(`/inventory/auto-order/pending?storeId=${storeId}`);
  },
  
  sendOrderNotification: async (storeId: number, items: any[]) => {
    return await inventoryApiInstance.post<any>(`/inventory/auto-order/notify?storeId=${storeId}`, { items });
  },
  
  getAllMenuItems: async (storeId: number) => {
    return await inventoryApiInstance.get<MenuItem[]>(`/inventory/menu-items?storeId=${storeId}`);
  },
  
  getMenuItemById: async (id: number) => {
    try {
      console.log(`메뉴 상세정보 요청 URL: ${inventoryApiInstance.defaults.baseURL}/menus/${id}`);
      const response = await inventoryApiInstance.get(`/menus/${id}?includeCookingMethods=true`);
      console.log('메뉴 상세정보 응답:', response.data);
      return response;
    } catch (error) {
      console.error(`메뉴 ID ${id} 조회 오류:`, error);
      throw error;
    }
  },
  
  createMenuItem: async (menuItem: Omit<MenuItem, 'id'>) => {
    return await inventoryApiInstance.post<MenuItem>('/inventory/menu-items', menuItem);
  },

  updateMenuItem: async (id: number, menuItem: Partial<MenuItem>) => {
    return await inventoryApiInstance.put<MenuItem>(`/inventory/menu-items/${id}`, menuItem);
  },

  deleteMenuItem: async (id: number) => {
    return await inventoryApiInstance.delete(`/inventory/menu-items/${id}`);
  },

  // 메뉴-식재료 관계 관리 메서드 추가
  getMenuItemIngredients: async (menuItemId: number) => {
    return await inventoryApiInstance.get<any[]>(`/inventory/menu-items/${menuItemId}/ingredients`);
  },

  addIngredientToMenuItem: async (menuItemId: number, ingredientId: number, quantity: number) => {
    return await inventoryApiInstance.post<any>(`/inventory/menu-items/${menuItemId}/ingredients/${ingredientId}`, quantity);
  },

  updateIngredientQuantity: async (menuItemId: number, ingredientId: number, quantity: number) => {
    return await inventoryApiInstance.put<any>(`/inventory/menu-items/${menuItemId}/ingredients/${ingredientId}`, quantity);
  },

  removeIngredientFromMenuItem: async (menuItemId: number, ingredientId: number) => {
    return await inventoryApiInstance.delete(`/inventory/menu-items/${menuItemId}/ingredients/${ingredientId}`);
  },

  adjustStock: async (ingredientId: number, quantity: number, reason: string) => {
    return await inventoryApiInstance.post<Ingredient>(`/inventory/ingredients/${ingredientId}/adjust`, {
      quantity,
      reason
    });
  },

  getStockHistory: async (storeId: number, startDate: string, endDate: string) => {
    return await inventoryApiInstance.get<any[]>(
      `/inventory/history?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}`
    );
  },

  // 입출고 기록 관련 API
  getStockTransactions: async (storeId: number, ingredientId?: number) => {
    try {
      const url = ingredientId 
        ? `/inventory/stock-transactions-with-batches?storeId=${storeId}&ingredientId=${ingredientId}`
        : `/inventory/stock-transactions-with-batches?storeId=${storeId}`;
      console.log(`입출고 기록 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      return response;
    } catch (error) {
      console.error('입출고 기록 조회 중 오류 발생:', error);
      throw error;
    }
  },

  // 배치 정보를 포함한 입출고 기록 조회
  getStockTransactionsWithBatches: async (storeId: number, ingredientId?: number) => {
    try {
      const url = ingredientId 
        ? `/inventory/stock-transactions-with-batches?storeId=${storeId}&ingredientId=${ingredientId}`
        : `/inventory/stock-transactions-with-batches?storeId=${storeId}`;
      console.log(`배치 포함 입출고 기록 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      console.log('배치 포함 입출고 기록 응답:', response.data);
      return response;
    } catch (error) {
      console.error('배치 포함 입출고 기록 조회 중 오류 발생:', error);
      throw error;
    }
  },

  createStockTransaction: async (transaction: any) => {
    try {
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      const transactionWithStore = { ...transaction, storeId };
      
      const url = '/inventory/stock-transactions';
      console.log(`입출고 기록 생성 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('입출고 기록 생성 데이터:', transactionWithStore);
      
      const response = await inventoryApiInstance.post(url, transactionWithStore);
      console.log('입출고 기록 생성 응답:', response.data);
      return response;
    } catch (error) {
      console.error('입출고 기록 생성 중 오류 발생:', error);
      throw error;
    }
  },

  // 소비기한 임박 식재료 조회
  getExpiringItems: async (storeId: number) => {
    try {
      const url = `/inventory/expiring-items?storeId=${storeId}`;
      console.log(`소비기한 임박 식재료 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      return response;
    } catch (error) {
      console.error('소비기한 임박 식재료 조회 중 오류 발생:', error);
      throw error;
    }
  },

  // 배치별 입고 처리
  createInboundWithBatches: async (request: any) => {
    try {
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      const requestWithStore = { ...request, storeId };
      
      // 배치 데이터는 사용자 입력 그대로 전송 (백엔드에서 용량 단위로 변환)
      // 변환 작업은 백엔드의 BatchManagementService.ProcessInboundBatchesAsync에서 수행
      
      const url = '/inventory/stock-transactions/inbound-batches';
      console.log(`배치별 입고 처리 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('배치별 입고 처리 데이터:', requestWithStore);
      
      const response = await inventoryApiInstance.post(url, requestWithStore);
      console.log('배치별 입고 처리 응답:', response.data);
      return response;
    } catch (error) {
      console.error('배치별 입고 처리 중 오류 발생:', error);
      throw error;
    }
  },

  // FIFO 기반 출고 처리
  createOutboundWithFifo: async (request: any) => {
    try {
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      const requestWithStore = { ...request, storeId };
      
      const url = '/inventory/stock-transactions/outbound-fifo';
      console.log(`FIFO 기반 출고 처리 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('FIFO 기반 출고 처리 데이터:', requestWithStore);
      
      const response = await inventoryApiInstance.post(url, requestWithStore);
      console.log('FIFO 기반 출고 처리 응답:', response.data);
      return response;
    } catch (error) {
      console.error('FIFO 기반 출고 처리 중 오류 발생:', error);
      throw error;
    }
  },

  // 특정 배치에서 출고 처리
  createOutboundFromBatch: async (request: any) => {
    try {
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      
      // 배치 ID가 없는 경우 오류 발생
      if (request.batchId === undefined || request.batchId === null) {
        throw new Error('배치 ID가 없습니다.');
      }
      
      // 배치 ID가 정수형인지 확인하고 필요시 변환
      let batchId = request.batchId;
      
      // 문자열인 경우 정수로 변환 시도
      if (typeof batchId === 'string') {
        const parsedId = parseInt(batchId, 10);
        if (!isNaN(parsedId)) {
          batchId = parsedId;
        } else {
          throw new Error(`유효하지 않은 배치 ID: ${batchId}, 타입: ${typeof batchId}`);
        }
      }
      
      // 정수형이 아닌 경우 오류 발생
      if (typeof batchId !== 'number' || isNaN(batchId)) {
        throw new Error(`유효하지 않은 배치 ID: ${batchId}, 타입: ${typeof batchId}`);
      }
      
      // 배치 ID 로깅
      console.log('출고 처리할 배치 ID:', batchId, '타입:', typeof batchId);
      
      // 요청 데이터 생성 (정수형 ID 사용)
      const requestWithStore = { 
        ...request, 
        storeId,
        batchId: batchId // 정수형 ID 사용
      };
      
      const url = '/inventory/stock-transactions/outbound-from-batch';
      console.log(`특정 배치 출고 처리 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('특정 배치 출고 처리 데이터:', requestWithStore);
      
      // API 호출
      const response = await inventoryApiInstance.post(url, requestWithStore);
      console.log('특정 배치 출고 처리 응답:', response.data);
      return response;
    } catch (error) {
      console.error('특정 배치 출고 처리 중 오류 발생:', error);
      throw error;
    }
  },

  // 만료된 배치 조회
  getExpiredBatches: async (storeId: number) => {
    try {
      const url = `/inventory/expired-batches?storeId=${storeId}`;
      console.log(`만료된 배치 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      return response;
    } catch (error) {
      console.error('만료된 배치 조회 중 오류 발생:', error);
      throw error;
    }
  },

  // 배치별 입출고 기록 조회
  getBatchTransactions: async (storeId: number, ingredientId?: number) => {
    try {
      let url = `/inventory/batch-transactions?storeId=${storeId}`;
      if (ingredientId) {
        url += `&ingredientId=${ingredientId}`;
      }
      console.log(`배치별 입출고 기록 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      return response;
    } catch (error) {
      console.error('배치별 입출고 기록 조회 중 오류 발생:', error);
      throw error;
    }
  },

  // 출고 시 배치 선택용 활성 배치 조회 (재고가 남아있는 모든 배치 반환)
  getActiveBatchesForOutbound: async (storeId: number, ingredientId: number) => {
    try {
      const url = `/inventory/active-batches-for-outbound?storeId=${storeId}&ingredientId=${ingredientId}`;
      console.log(`활성 배치 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      return response;
    } catch (error) {
      console.error('활성 배치 조회 중 오류 발생:', error);
      throw error;
    }
  },

  // 소진된 배치 정리
  cleanupConsumedBatches: async (storeId: number) => {
    try {
      const url = `/inventory/cleanup-consumed-batches?storeId=${storeId}`;
      console.log(`소진된 배치 정리 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.post(url);
      return response;
    } catch (error) {
      console.error('소진된 배치 정리 중 오류 발생:', error);
      throw error;
    }
  },

  // 카테고리 관리
  getCategories: async (storeId: number) => {
    return await inventoryApiInstance.get<string[]>(`/inventory/menu-items/categories?storeId=${storeId}`);
  },

  addCategory: async (storeId: number, category: string) => {
    return await inventoryApiInstance.post<string>(`/inventory/menu-items/categories?storeId=${storeId}`, JSON.stringify(category), {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },

  updateCategory: async (storeId: number, oldCategory: string, newCategory: string) => {
    return await inventoryApiInstance.put<void>(
      `/inventory/menu-items/categories/${encodeURIComponent(oldCategory)}?storeId=${storeId}`, 
      JSON.stringify(newCategory),
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
  },

  deleteCategory: async (storeId: number, category: string) => {
    return await inventoryApiInstance.delete(`/inventory/menu-items/categories/${encodeURIComponent(category)}?storeId=${storeId}`);
  },

  // 재고 목록 조회 API
  getIngredients: async (storeId: number) => {
    try {
      const url = `/inventory/ingredients?storeId=${storeId}`;
      console.log(`식재료 목록 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      return response;
    } catch (error) {
      console.error(`식재료 목록 조회 중 오류 발생:`, error);
      throw error;
    }
  },
  
  // 공급업체 관리
  getSuppliers: async (storeId: number) => {
    try {
      // API 요청 URL 로깅
      const url = `/supply-chain/suppliers?storeId=${storeId}`;
      console.log(`공급업체 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      // API 호출
      const response = await inventoryApiInstance.get(url);
      console.log('API 공급업체 응답 상태:', response.status);
      console.log('API 공급업체 응답 데이터:', response.data);
      return response;
    } catch (error) {
      console.error('공급업체 데이터 조회 오류:', error);
      throw error;
    }
  },
  
  createSupplier: async (supplier: Omit<Supplier, 'id'>) => {
    try {
      // storeId가 없는 경우 로컬 스토리지에서 가져옴
      if (!supplier.storeId) {
        const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
        supplier = { ...supplier, storeId };
      }
      
      // API 요청 URL 로깅
      const url = '/supply-chain/suppliers';
      console.log(`공급업체 생성 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('공급업체 생성 요청 데이터:', supplier);
      
      // API 호출
      const response = await inventoryApiInstance.post(url, supplier);
      console.log('API 공급업체 생성 응답 상태:', response.status);
      console.log('API 공급업체 생성 응답 데이터:', response.data);
      return response;
    } catch (error) {
      console.error('공급업체 생성 오류:', error);
      throw error;
    }
  },
  
  updateSupplier: async (id: number, supplier: Partial<Supplier>) => {
    try {
      const response = await inventoryApiInstance.put(`/supply-chain/suppliers/${id}`, supplier);
      return response;
    } catch (error) {
      console.error(`공급업체 ID ${id} 업데이트 오류:`, error);
      throw error;
    }
  },
  
  deleteSupplier: async (id: number) => {
    try {
      // 스토어 ID 가져오기
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      
      // API 요청 URL 로깅
      const url = `/supply-chain/suppliers/${id}?storeId=${storeId}`;
      console.log(`공급업체 삭제 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      // API 호출
      const response = await inventoryApiInstance.delete(url);
      console.log('API 공급업체 삭제 응답 상태:', response.status);
      return response;
    } catch (error: any) {
      console.error(`공급업체 ID ${id} 삭제 오류:`, error);
      
      // 오류 응답 상세 로깅
      if (error.response) {
        console.error('오류 상태 코드:', error.response.status);
        console.error('오류 응답 데이터:', error.response.data);
      }
      
      throw error;
    }
  },

  // 작업대 목록 조회 API (대시보드에서 사용)
  getWorkStations: async (storeId: number) => {
    try {
      // API 요청 URL 로깅
      const url = `/work-management/workstations?storeId=${storeId}`;
      console.log(`작업대 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      // API 호출
      const response = await inventoryApiInstance.get(url);
      // 정규화: API가 객체형태로 올 가능성 대비
      if (response && response.data && !Array.isArray(response.data) && response.data.items) {
        return { ...response, data: response.data.items } as any;
      }
      console.log('API 작업대 응답 상태:', response.status);
      console.log('API 작업대 응답 데이터:', response.data);
      return response;
    } catch (error) {
      console.error('작업대 데이터 조회 오류:', error);
      throw error;
    }
  },

  // 재고 차감 처리 (새로 추가)
  deductInventoryForOrder: (orderId: number) => api.post(`/inventory/deduct-for-order/${orderId}`), // fixed double api path - using centralized endpoint
  
  // 재고 수량 조정 (새로 추가)
      adjustIngredientStock: (ingredientId: number, adjustment: { quantity: number; reason: string }) => 
      api.post(`/inventory/ingredients/${ingredientId}/adjust`, adjustment), // fixed double api path - using centralized endpoint

  // 메뉴 API 메서드들 추가
  getMenus: async (storeId: number, params?: { 
    type?: string,
    isActive?: boolean,
    page?: number, 
    limit?: number,
    searchTerm?: string
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('storeId', storeId.toString());
  
    if (params) {
      if (params.type) queryParams.append('type', params.type);
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.searchTerm) queryParams.append('search', params.searchTerm);
    }
    
    try {
      // API 요청 URL 로깅
      const url = `/menus?${queryParams.toString()}`;
      console.log(`메뉴 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      // API 호출
      const response = await inventoryApiInstance.get(url);
      console.log('메뉴 API 응답 상태:', response.status);
      console.log('메뉴 API 응답 데이터:', response.data);
      return response;
    } catch (error) {
      console.error('메뉴 데이터 조회 오류:', error);
      throw error;
    }
  },

  getMenu: async (id: number) => {
    try {
      console.log(`메뉴 상세정보 요청 URL: ${inventoryApiInstance.defaults.baseURL}/menus/${id}`);
      const response = await inventoryApiInstance.get(`/menus/${id}?includeCookingMethods=true`);
      console.log('메뉴 상세정보 응답:', response.data);
      return response;
    } catch (error) {
      console.error(`메뉴 ID ${id} 조회 오류:`, error);
      throw error;
    }
  },

  // 거래처별 연결된 식재료 조회
  getSupplierIngredients: async (supplierId: number, storeId: number) => {
    try {
      const url = `/supply-chain/suppliers/${supplierId}/ingredients?storeId=${storeId}`;
      console.log(`거래처 식재료 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      console.log('거래처 식재료 응답:', response.data);
      return response;
    } catch (error) {
      console.error('거래처 식재료 조회 실패:', error);
      // 에러 발생 시 빈 배열 반환하여 빌드 오류 방지
      return { data: { ingredients: [] } } as any;
    }
  },

  getSupplierPackagingContainers: async (supplierId: number, storeId: number) => {
    try {
      const url = `/supply-chain/suppliers/${supplierId}/packaging-containers?storeId=${storeId}`;
      console.log(`거래처 포장용기 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      console.log('거래처 포장용기 응답:', response.data);
      return response;
    } catch (error) {
      console.error('거래처 포장용기 조회 실패:', error);
      // 에러 발생 시 빈 배열 반환하여 빌드 오류 방지
      return { data: { packagingContainers: [] } } as any;
    }
  },

  // 식재료별 사용 중인 메뉴 조회
  getIngredientMenus: async (ingredientId: number, storeId: number) => {
    try {
      const url = `/inventory/ingredients/${ingredientId}/menus?storeId=${storeId}`;
      console.log(`식재료 메뉴 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      console.log('식재료 메뉴 응답:', response.data);
      return response;
    } catch (error) {
      console.error('식재료 메뉴 조회 실패:', error);
      throw error;
    }
  },
};

// 포장용기 API
const packagingContainerApi = {
  getPackagingContainers: async (storeId: number) => {
    return await inventoryApiInstance.get<import('../types').PackagingContainer[]>(`/inventory/packaging-containers?storeId=${storeId}`);
  },
  
  getPackagingContainerById: async (id: number) => {
    return await inventoryApiInstance.get<import('../types').PackagingContainer>(`/inventory/packaging-containers/${id}`);
  },
  
  createPackagingContainer: async (packagingContainer: Omit<import('../types').PackagingContainer, 'id'>) => {
    try {
      // storeId가 없는 경우 로컬 스토리지에서 가져옴
      if (!packagingContainer.storeId) {
        const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
        packagingContainer = { ...packagingContainer, storeId };
      }
      
      const url = '/inventory/packaging-containers';
      console.log(`포장용기 생성 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('포장용기 생성 요청 데이터:', packagingContainer);
      
      const response = await inventoryApiInstance.post<PackagingContainer>(url, packagingContainer);
      console.log('API 포장용기 생성 응답 상태:', response.status);
      console.log('API 포장용기 생성 응답 데이터:', response.data);
      return response;
    } catch (error) {
      console.error('포장용기 생성 오류:', error);
      throw error;
    }
  },

  updatePackagingContainer: async (id: number, packagingContainer: Partial<PackagingContainer>) => {
    try {
      const url = `/inventory/packaging-containers/${id}`;
      console.log(`포장용기 수정 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('포장용기 수정 요청 데이터:', packagingContainer);
      
      const response = await inventoryApiInstance.put<PackagingContainer>(url, packagingContainer);
      console.log('API 포장용기 수정 응답 상태:', response.status);
      console.log('API 포장용기 수정 응답 데이터:', response.data);
      return response;
    } catch (error) {
      console.error(`포장용기 ID ${id} 수정 오류:`, error);
      throw error;
    }
  },

  deletePackagingContainer: async (id: number) => {
    try {
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      const url = `/inventory/packaging-containers/${id}?storeId=${storeId}`;
      console.log(`포장용기 삭제 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.delete(url);
      console.log('API 포장용기 삭제 응답 상태:', response.status);
      return response;
    } catch (error: any) {
      console.error(`포장용기 ID ${id} 삭제 오류:`, error);
      throw error;
    }
  },
  
  getLowStockPackagingContainers: async (storeId: number) => {
    return await inventoryApiInstance.get<PackagingContainer[]>(`/inventory/packaging-containers/low-stock?storeId=${storeId}`);
  },

  getPackagingContainerMenus: async (packagingContainerId: number, storeId: number) => {
    try {
      const url = `/inventory/packaging-containers/${packagingContainerId}/menus?storeId=${storeId}`;
      console.log(`포장용기 메뉴 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      console.log('포장용기 메뉴 응답:', response.data);
      return response;
    } catch (error) {
      console.error('포장용기 메뉴 조회 실패:', error);
      throw error;
    }
  },

  adjustStock: async (packagingContainerId: number, quantity: number, reason: string) => {
    return await inventoryApiInstance.post<PackagingContainer>(`/inventory/packaging-containers/${packagingContainerId}/adjust`, {
      quantity,
      reason
    });
  },
};

// 포장용기 입출고 기록 API
const packagingContainerTransactionApi = {
  getStockTransactions: async (storeId: number, packagingContainerId?: number) => {
    try {
      let url = `/inventory/packaging-containers/stock-transactions?storeId=${storeId}`;
      if (packagingContainerId) {
        url += `&packagingContainerId=${packagingContainerId}`;
      }
      console.log(`포장용기 입출고 기록 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      return response;
    } catch (error) {
      console.error('포장용기 입출고 기록 조회 중 오류 발생:', error);
      throw error;
    }
  },

  // 배치 정보를 포함한 입출고 기록 조회
  getStockTransactionsWithBatches: async (storeId: number, packagingContainerId?: number) => {
    try {
      let url = `/inventory/packaging-containers/stock-transactions-with-batches?storeId=${storeId}`;
      if (packagingContainerId) {
        url += `&packagingContainerId=${packagingContainerId}`;
      }
      console.log(`포장용기 배치 포함 입출고 기록 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      console.log('포장용기 배치 포함 입출고 기록 응답:', response.data);
      return response;
    } catch (error) {
      console.error('포장용기 배치 포함 입출고 기록 조회 중 오류 발생:', error);
      throw error;
    }
  },

  // 배치별 입출고 기록 조회
  getBatchTransactions: async (storeId: number, packagingContainerId?: number) => {
    try {
      let url = `/inventory/packaging-containers/batch-transactions?storeId=${storeId}`;
      if (packagingContainerId) {
        url += `&packagingContainerId=${packagingContainerId}`;
      }
      console.log(`포장용기 배치별 입출고 기록 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      console.log('포장용기 배치별 입출고 기록 응답:', response.data);
      return response;
    } catch (error) {
      console.error('포장용기 배치별 입출고 기록 조회 중 오류 발생:', error);
      throw error;
    }
  },

  // 출고 시 배치 선택용 활성 배치 조회 (재고가 남아있는 모든 배치 반환, 제한 없음)
  getActiveBatchesForOutbound: async (storeId: number, packagingContainerId: number) => {
    try {
      const url = `/inventory/packaging-containers/active-batches-for-outbound?storeId=${storeId}&packagingContainerId=${packagingContainerId}`;
      console.log(`포장용기 활성 배치 조회 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      const response = await inventoryApiInstance.get(url);
      console.log('포장용기 활성 배치 응답:', response.data);
      return response;
    } catch (error) {
      console.error('포장용기 활성 배치 조회 중 오류 발생:', error);
      throw error;
    }
  },

  // 배치별 입고 처리
  createInboundWithBatches: async (packagingContainerId: number, batches: Array<{ quantity: number }>, reason: string, unitPrice?: number, totalPrice?: number) => {
    try {
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      const request = {
        packagingContainerId,
        batches,
        reason,
        storeId,
        unitPrice,
        totalPrice
      };
      const url = '/inventory/packaging-containers/stock-transactions/inbound-batches';
      console.log(`포장용기 배치별 입고 처리 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('포장용기 배치별 입고 데이터:', request);
      const response = await inventoryApiInstance.post(url, request);
      console.log('포장용기 배치별 입고 응답:', response.data);
      return response;
    } catch (error) {
      console.error('포장용기 배치별 입고 처리 중 오류 발생:', error);
      throw error;
    }
  },

  // FIFO 기반 출고 처리
  createOutboundWithFifo: async (packagingContainerId: number, quantity: number, reason: string) => {
    try {
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      const request = {
        packagingContainerId,
        quantity,
        reason,
        storeId
      };
      const url = '/inventory/packaging-containers/stock-transactions/outbound-fifo';
      console.log(`포장용기 FIFO 기반 출고 처리 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('포장용기 FIFO 기반 출고 데이터:', request);
      const response = await inventoryApiInstance.post(url, request);
      console.log('포장용기 FIFO 기반 출고 응답:', response.data);
      return response;
    } catch (error) {
      console.error('포장용기 FIFO 기반 출고 처리 중 오류 발생:', error);
      throw error;
    }
  },

  // 특정 배치 출고 처리
  createOutboundFromBatch: async (batchId: number, packagingContainerId: number, quantity: number, reason: string) => {
    try {
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      const request = {
        batchId,
        packagingContainerId,
        quantity,
        reason,
        storeId
      };
      const url = '/inventory/packaging-containers/stock-transactions/outbound-from-batch';
      console.log(`포장용기 특정 배치 출고 처리 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('포장용기 특정 배치 출고 데이터:', request);
      const response = await inventoryApiInstance.post(url, request);
      console.log('포장용기 특정 배치 출고 응답:', response.data);
      return response;
    } catch (error) {
      console.error('포장용기 특정 배치 출고 처리 중 오류 발생:', error);
      throw error;
    }
  },

  createStockTransaction: async (transaction: Omit<PackagingContainerTransaction, 'id' | 'createdAt'> & { packagingContainerId: number; type: 'IN' | 'OUT'; quantity: number; reason: string; createdBy?: string; storeId?: number; packagingContainerName?: string; previousQuantity?: number; newQuantity?: number }) => {
    try {
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      const transactionWithStore = { ...transaction, storeId };
      
      const url = '/inventory/packaging-containers/stock-transactions';
      console.log(`포장용기 입출고 기록 생성 요청: ${inventoryApiInstance.defaults.baseURL}${url}`);
      console.log('포장용기 입출고 기록 생성 데이터:', transactionWithStore);
      
      const response = await inventoryApiInstance.post(url, transactionWithStore);
      console.log('포장용기 입출고 기록 생성 응답:', response.data);
      return response;
    } catch (error) {
      console.error('포장용기 입출고 기록 생성 중 오류 발생:', error);
      throw error;
    }
  },
};

// 공급망 API
const supplyChainApi = {
  getAllSuppliers: async (storeId: number) => {
    return await inventoryApiInstance.get<Supplier[]>(`/supply-chain/suppliers?storeId=${storeId}`);
  },
  
  getSupplierById: async (id: number) => {
    return await inventoryApiInstance.get<Supplier>(`/supply-chain/suppliers/${id}`);
  },

  createSupplier: async (supplier: Omit<Supplier, 'id'>) => {
    return await inventoryApiInstance.post<Supplier>('/supply-chain/suppliers', supplier);
  },

  updateSupplier: async (id: number, supplier: Partial<Supplier>) => {
    return await inventoryApiInstance.put<Supplier>(`/supply-chain/suppliers/${id}`, supplier);
  },

  deleteSupplier: async (id: number) => {
    return await inventoryApiInstance.delete(`/supply-chain/suppliers/${id}`);
  },
  
  getPurchaseOrders: async (storeId: number, params?: {
    status?: PurchaseOrderStatus,
    page?: number,
    limit?: number,
    startDate?: string,
    endDate?: string
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('storeId', storeId.toString());
    
    if (params) {
      if (params.status) queryParams.append('status', params.status);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
    }
    
    return await inventoryApiInstance.get<PurchaseOrder[]>(`/supply-chain/purchase-orders?${queryParams.toString()}`);
  },
  
  getPurchaseOrderById: async (id: number) => {
    return await inventoryApiInstance.get<PurchaseOrder>(`/supply-chain/purchase-orders/${id}`);
  },
  
  createPurchaseOrder: async (order: Omit<PurchaseOrder, 'id'>) => {
    return await inventoryApiInstance.post<PurchaseOrder>('/supply-chain/purchase-orders', order);
  },
  
  updatePurchaseOrderStatus: async (id: number, status: PurchaseOrderStatus) => {
    return await inventoryApiInstance.put<PurchaseOrder>(`/supply-chain/purchase-orders/${id}/status`, { status });
  },

  updatePurchaseOrder: async (id: number, order: Partial<PurchaseOrder>) => {
    return await inventoryApiInstance.put<PurchaseOrder>(`/supply-chain/purchase-orders/${id}`, order);
  },

  deletePurchaseOrder: async (id: number) => {
    return await inventoryApiInstance.delete(`/supply-chain/purchase-orders/${id}`);
  },

  receivePurchaseOrder: async (id: number, receivedItems: Array<{id: number, receivedQuantity: number}>) => {
    return await inventoryApiInstance.post<PurchaseOrder>(`/supply-chain/purchase-orders/${id}/receive`, { receivedItems });
  },
  
  // 자동 발주 관련 메서드 추가
  getAutoOrderCandidates: async (storeId: number) => {
    return await inventoryApiInstance.get<any[]>(`/supply-chain/auto-order/candidates?storeId=${storeId}`);
  },
  
  createOrderFromLowStock: async (
    storeId: number, 
    supplierId: number, 
    options?: { 
      includeItems?: number[], 
      excludeItems?: number[] 
    }
  ) => {
    return await inventoryApiInstance.post<PurchaseOrder>('/supply-chain/auto-order/create', {
      storeId,
      supplierId,
      includeItems: options?.includeItems || [],
      excludeItems: options?.excludeItems || []
    });
  },
  
  processPendingOrders: async (storeId: number) => {
    return await inventoryApiInstance.post<{count: number}>(`/supply-chain/auto-order/process?storeId=${storeId}`);
  },
  
  sendSupplierNotification: async (orderId: number) => {
    return await inventoryApiInstance.post<any>(`/supply-chain/purchase-orders/${orderId}/notify-supplier`);
  }
};

// 분석 API
const analyticsApi = {
  getDashboardMetrics: async (storeId: number) => {
    return await inventoryApiInstance.get<any>(`/analytics/dashboard?storeId=${storeId}`);
  },

  getStoreMetrics: async (storeId: number, startDate: string, endDate: string) => {
    return await inventoryApiInstance.get<StoreMetrics[]>(
      `/analytics/metrics?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}`
    );
  },
  
  getInventoryPredictions: async (storeId: number) => {
    return await inventoryApiInstance.get<InventoryPrediction[]>(`/analytics/inventory-predictions?storeId=${storeId}`);
  },
  
  getSalesInsights: async (storeId: number) => {
    return await inventoryApiInstance.get<SalesInsight[]>(`/analytics/insights?storeId=${storeId}`);
  },

  getSalesAnalytics: async (storeId: number, startDate: string, endDate: string, groupBy: 'day' | 'week' | 'month') => {
    return await inventoryApiInstance.get<any[]>(
      `/analytics/sales?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}&groupBy=${groupBy}`
    );
  },

  getTopSellingItems: async (storeId: number, startDate: string, endDate: string, limit = 10) => {
    return await inventoryApiInstance.get<any[]>(
      `/analytics/top-items?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}&limit=${limit}`
    );
  },

  getCustomerAnalytics: async (storeId: number, startDate: string, endDate: string) => {
    return await inventoryApiInstance.get<any>(
      `/analytics/customers?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}`
    );
  },

  exportAnalytics: async (storeId: number, reportType: string, startDate: string, endDate: string, format: 'csv' | 'excel') => {
    return await inventoryApiInstance.get<Blob>(
      `/analytics/export?storeId=${storeId}&reportType=${reportType}&startDate=${startDate}&endDate=${endDate}&format=${format}`,
      { responseType: 'blob' }
    );
  },

  // 매입 비용 분석 데이터 조회
  getPurchaseCostAnalytics: async (storeId: number, startDate: string, endDate: string, ingredientId?: number) => {
    try {
      let url = `/analytics/purchase-cost/${storeId}?startDate=${startDate}&endDate=${endDate}`;
      if (ingredientId) {
        url += `&ingredientId=${ingredientId}`;
      }
      return await inventoryApiInstance.get<any[]>(url);
    } catch (error) {
      console.error('매입 비용 분석 데이터 조회 중 오류:', error);
      throw error;
    }
  },

  // 기간 내 FIFO 원가 합계 (순이익용 totalPurchase - 식재료·포장용기 출고 기준)
  getPurchaseCostFifo: async (storeId: number, startDate: string, endDate: string) => {
    try {
      const url = `/analytics/purchase-cost-fifo?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}`;
      return await inventoryApiInstance.get<{ totalPurchaseFifo: number; ingredientFifo?: number; packagingFifo?: number; unit?: string }>(url);
    } catch (error) {
      console.error('FIFO 매입 원가 조회 중 오류:', error);
      throw error;
    }
  },

  // 매장 운영 정보 조회. asOfDate 있으면 해당 일자(yyyy-MM-dd) end-of-day에 유효한 스냅샷 반환.
  getStoreOperationInfo: async (storeId: number, asOfDate?: string) => {
    const isDevMode = process.env.NODE_ENV === 'development';
    const enableMockData = process.env.REACT_APP_ENABLE_MOCK_DATA === 'true';

    if (isDevMode && enableMockData) {
      const { generateMockStoreOperationInfo } = await import('./mockDataService');
      const mockData = generateMockStoreOperationInfo(storeId);
      console.log('🛠️ [Mock Data] 운영 정보 Mock 데이터 반환:', mockData);
      return {
        data: mockData,
        status: 200,
        statusText: 'OK',
      };
    }

    const query = asOfDate ? `?asOfDate=${encodeURIComponent(asOfDate)}` : '';
    try {
      return await inventoryApiInstance.get<any>(`/analytics/store-operation-info/${storeId}${query}`);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('매장 운영 정보 조회 중 오류:', error);
      throw error;
    }
  },

  // 매장 운영 정보 버전 목록 (기간 내 수정 시점별 스냅샷). from/to = yyyy-MM-dd
  getStoreOperationInfoVersions: async (storeId: number, fromDate: string, toDate: string) => {
    const isDevMode = process.env.NODE_ENV === 'development';
    const enableMockData = process.env.REACT_APP_ENABLE_MOCK_DATA === 'true';

    if (isDevMode && enableMockData) {
      const { generateMockStoreOperationInfo } = await import('./mockDataService');
      const snapshot = generateMockStoreOperationInfo(storeId);
      const from = new Date(fromDate);
      const to = new Date(toDate);
      return {
        data: [{ effectiveFrom: from.toISOString(), effectiveTo: to.toISOString(), snapshot }],
        status: 200,
        statusText: 'OK',
      };
    }

    const query = `?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`;
    const res = await inventoryApiInstance.get<Array<{ effectiveFrom: string; effectiveTo: string; snapshot: any }>>(
      `/analytics/store-operation-info/${storeId}/versions${query}`
    );
    return res;
  },

  // 매장 운영 정보 존재 여부 확인
  checkStoreOperationInfoExists: async (storeId: number) => {
    try {
      const response = await inventoryApiInstance.get<{ exists: boolean }>(`/analytics/store-operation-info/${storeId}/exists`);
      return response.data.exists;
    } catch (error) {
      console.error('매장 운영 정보 존재 확인 중 오류:', error);
      throw error;
    }
  },

  // 매장 운영 정보 생성 또는 업데이트
  createOrUpdateStoreOperationInfo: async (operationInfo: any) => {
    try {
      return await inventoryApiInstance.post<any>(`/analytics/store-operation-info`, operationInfo);
    } catch (error) {
      console.error('매장 운영 정보 저장 중 오류:', error);
      throw error;
    }
  },

  // 새로운 Analytics API 엔드포인트들
  // 매출 분석 데이터 조회
  getSalesAnalyticsData: async (storeId: number, startDate: string, endDate: string, platform?: string) => {
    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate
      });
      if (platform && platform !== '전체') {
        queryParams.append('platform', platform);
      }
      
      const url = `/analytics/sales/${storeId}?${queryParams.toString()}`;
      console.log(`매출 분석 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('매출 분석 응답:', response.data);
      return response;
    } catch (error) {
      console.error('매출 분석 데이터 조회 오류:', error);
      throw error;
    }
  },

  // 재고 사용량 분석 데이터 조회 (올바른 방식)
  getInventoryUsageAnalytics: async (storeId: number, ingredientId: number, startDate: string, endDate: string) => {
    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate
      });
      
      const url = `/analytics/inventory-usage/${storeId}/${ingredientId}?${queryParams.toString()}`;
      console.log(`재고 사용량 분석 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('재고 사용량 분석 응답:', response.data);
      return response;
    } catch (error) {
      console.error('재고 사용량 분석 데이터 조회 오류:', error);
      throw error;
    }
  },

  // 메뉴 판매 분석 데이터 조회
  getMenuSalesAnalytics: async (storeId: number, menuId: number, startDate: string, endDate: string) => {
    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate
      });
      
      const url = `/analytics/menu-sales/${storeId}/${menuId}?${queryParams.toString()}`;
      console.log(`메뉴 판매 분석 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('메뉴 판매 분석 응답:', response.data);
      return response;
    } catch (error) {
      console.error('메뉴 판매 분석 데이터 조회 오류:', error);
      throw error;
    }
  },

  // 일일 집계 데이터 조회
  getDailySummaries: async (storeId: number, startDate: string, endDate: string) => {
    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate
      });
      
      const url = `/analytics/daily-summary/${storeId}?${queryParams.toString()}`;
      console.log(`일일 집계 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('일일 집계 응답:', response.data);
      return response;
    } catch (error) {
      console.error('일일 집계 데이터 조회 오류:', error);
      throw error;
    }
  },

  // 재고 분석용 식재료 목록 조회
  getIngredientsForAnalytics: async (storeId: number) => {
    try {
      const url = `/analytics/ingredients/${storeId}`;
      console.log(`재고 분석용 식재료 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('재고 분석용 식재료 응답:', response.data);
      return response;
    } catch (error) {
      console.error('재고 분석용 식재료 목록 조회 오류:', error);
      throw error;
    }
  },

  // 메뉴 분석용 메뉴 목록 조회 (메인/옵션 구분)
  getMenusForAnalytics: async (storeId: number) => {
    try {
      const url = `/analytics/menus/${storeId}`;
      console.log(`메뉴 분석용 메뉴 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('메뉴 분석용 메뉴 응답:', response.data);
      return response;
    } catch (error) {
      console.error('메뉴 분석용 메뉴 목록 조회 오류:', error);
      throw error;
    }
  },

  // ❌ 제거: 이 API는 더 이상 필요하지 않음 - 분석 DB 전용 사용으로 변경
  // getStockTransactionsForAnalytics: async (storeId: number, startDate: string, endDate: string, ingredientId?: number, type?: string) => {

  // 주문 데이터에서 매출 분석 데이터 생성 (주문관리 탭 데이터 활용)
  generateSalesFromOrders: async (storeId: number, date: string) => {
    return await inventoryApiInstance.post<any>('/analytics/generate-sales-from-orders', {
      storeId,
      date
    });
  },

  // ==================== 매장분석 API ====================

  // 인기 메뉴(메인메뉴) TOP5 조회
  getPopularMenus: async (storeId: number, startDate: string, endDate: string) => {
    try {
      const url = API_ENDPOINTS.ANALYTICS.POPULAR_MENUS(storeId, startDate, endDate);
      console.log(`인기 메뉴 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('인기 메뉴 응답:', response.data);
      return response;
    } catch (error) {
      console.error('인기 메뉴 조회 오류:', error);
      throw error;
    }
  },

  // 인기 옵션(옵션메뉴) TOP5 조회
  getPopularOptions: async (storeId: number, startDate: string, endDate: string) => {
    try {
      const url = API_ENDPOINTS.ANALYTICS.POPULAR_OPTIONS(storeId, startDate, endDate);
      console.log(`인기 옵션 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('인기 옵션 응답:', response.data);
      return response;
    } catch (error) {
      console.error('인기 옵션 조회 오류:', error);
      throw error;
    }
  },

  // 인기 식재료 TOP5 조회
  getPopularIngredients: async (storeId: number, startDate: string, endDate: string) => {
    try {
      const url = API_ENDPOINTS.ANALYTICS.POPULAR_INGREDIENTS(storeId, startDate, endDate);
      console.log(`인기 식재료 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('인기 식재료 응답:', response.data);
      return response;
    } catch (error) {
      console.error('인기 식재료 조회 오류:', error);
      throw error;
    }
  },

  // 매출추이 조회
  getRevenueTrend: async (storeId: number, startDate: string, endDate: string) => {
    try {
      const url = API_ENDPOINTS.ANALYTICS.REVENUE_TREND(storeId, startDate, endDate);
      console.log(`매출추이 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('매출추이 응답:', response.data);
      return response;
    } catch (error) {
      console.error('매출추이 조회 오류:', error);
      throw error;
    }
  },

  // 폐기 분석 데이터 조회 - 기간별 통계
  getWasteAnalytics: async (storeId: number, startDate: string, endDate: string) => {
    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate
      });
      const url = `/analytics/waste/${storeId}?${queryParams.toString()}`;
      console.log(`폐기 분석 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('폐기 분석 응답:', response.data);
      return response;
    } catch (error) {
      console.error('폐기 분석 데이터 조회 오류:', error);
      throw error;
    }
  },

  // 폐기 분석 데이터 조회 - 월별 트렌드
  getWasteMonthlyTrend: async (storeId: number, year: number) => {
    try {
      const queryParams = new URLSearchParams({
        year: year.toString()
      });
      const url = `/analytics/waste/monthly-trend/${storeId}?${queryParams.toString()}`;
      console.log(`폐기 월별 트렌드 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('폐기 월별 트렌드 응답:', response.data);
      return response;
    } catch (error) {
      console.error('폐기 월별 트렌드 조회 오류:', error);
      throw error;
    }
  },

  // 폐기 분석 데이터 조회 - 폐기 비용 Top6 식재료
  getWasteTopIngredientsByCost: async (storeId: number, startDate: string, endDate: string) => {
    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate
      });
      const url = `/analytics/waste/top-by-cost/${storeId}?${queryParams.toString()}`;
      console.log(`폐기 비용 Top6 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('폐기 비용 Top6 응답:', response.data);
      return response;
    } catch (error) {
      console.error('폐기 비용 Top6 식재료 조회 오류:', error);
      throw error;
    }
  },

  // 폐기 분석 데이터 조회 - 폐기 건수 Top6 식재료
  getWasteTopIngredientsByCount: async (storeId: number, startDate: string, endDate: string) => {
    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate
      });
      const url = `/analytics/waste/top-by-count/${storeId}?${queryParams.toString()}`;
      console.log(`폐기 건수 Top6 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('폐기 건수 Top6 응답:', response.data);
      return response;
    } catch (error) {
      console.error('폐기 건수 Top6 식재료 조회 오류:', error);
      throw error;
    }
  },
  
  // 메뉴 조회 (대시보드 셋업 가이드용)
  getAllMenus: async (storeId: number, params?: { 
  type?: string,
  isActive?: boolean,
    page?: number, 
    limit?: number,
    searchTerm?: string
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('storeId', storeId.toString());
  
    if (params) {
      if (params.type) queryParams.append('type', params.type);
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.searchTerm) queryParams.append('search', params.searchTerm);
    }
    
    try {
      // API 요청 URL 로깅
      const url = `/menus?${queryParams.toString()}`;
      console.log(`메뉴 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      // API 호출
      const response = await inventoryApiInstance.get(url);
      console.log('메뉴 API 응답 상태:', response.status);
      console.log('메뉴 API 응답 데이터 구조:', {
        hasData: !!response.data,
        dataType: typeof response.data,
        isArray: Array.isArray(response.data),
        dataKeys: response.data ? Object.keys(response.data) : [],
        hasMenus: response.data && response.data.menus ? true : false,
        menusType: response.data && response.data.menus ? typeof response.data.menus : null,
        isMenusArray: response.data && response.data.menus ? Array.isArray(response.data.menus) : null,
        menusLength: response.data && response.data.menus && Array.isArray(response.data.menus) ? response.data.menus.length : null,
        totalCount: response.data && response.data.totalCount ? response.data.totalCount : null,
        firstItem: response.data && Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null,
        firstMenusItem: response.data && response.data.menus && Array.isArray(response.data.menus) && response.data.menus.length > 0 ? response.data.menus[0] : null
      });
      console.log('메뉴 API 응답 데이터(전체):', response.data);
      return response;
    } catch (error) {
      console.error('메뉴 데이터 조회 오류:', error);
      throw error;
    }
  },

  // 통계분석/재고분석/보정탭의 "사용량 로그" 테이블을 위한 API
  // 실제 입출기록의 개별 트랜잭션 데이터를 반환
  getStockTransactionsForAnalytics: async (storeId: number, ingredientId: number, startDate: string, endDate: string) => {
    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate
      });
      
      const url = `/inventory/stock-transactions-for-analytics?storeId=${storeId}&ingredientId=${ingredientId}&${queryParams.toString()}`;
      console.log(`분석용 입출고 기록 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      const response = await inventoryApiInstance.get(url);
      console.log('분석용 입출고 기록 응답:', response.data);
      return response;
    } catch (error) {
      console.error('분석용 입출고 기록 조회 오류:', error);
      throw error;
    }
  },

  /** 일일 리포트 예상순이익: FIFO 대신 매입 분석 합을 쓴 날 감사 로그(백엔드 적재). */
  reportDailyReportCostBasis: async (payload: {
    storeId: number;
    reportDate: string;
    days: Array<{ businessDate: string; fifoTotal: number | null; purchaseAnalyticsSum: number; reason: string }>;
  }) => {
    try {
      return await inventoryApiInstance.post('/analytics/daily-report-cost-basis', payload);
    } catch (error) {
      console.warn('일일리포트 원가 폴백 로그 기록 실패:', error);
      throw error;
    }
  },
};

// 테스트 API
const testApi = {
  testConnection: async () => {
    try {
      const originalBaseUrl = inventoryApiInstance.defaults.baseURL;
      
      // fixed double api path - use centralized endpoint for testing
      const testUrl = API_ENDPOINTS.TEST.CONNECTION;
      console.log('연결 테스트 URL:', testUrl);
      console.log('원래 baseURL:', originalBaseUrl);
      console.log('localStorage apiUrl:', localStorage.getItem('apiUrl'));
      
      // 백엔드 연결 테스트를 위한 간단한 엔드포인트 직접 호출
      const response = await safeApiCall.gateway.get(testUrl);
      console.log('API 연결 테스트 응답:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('API 연결 테스트 오류:', error);
      console.error('오류 상세 정보:', error.response ? error.response.status : '요청 실패');
      throw error;
    }
  },
  
  clearLocalStorage: () => {
    // 로컬 스토리지에서 사용자 관련 항목 모두 삭제 (토큰과 StoreId, 알림 관련 데이터 제외)
    const keysToKeep = [
      'token', 
      'storeId', 
      'apiUrl', 
      'apiLogging',
      // 알림 관련 데이터 보호
      'notificationSettings',
      'notificationSubscriptionState',
      'pwaInstalled',
      'pwaInstallDismissed'
    ];
    
    Object.keys(localStorage).forEach(key => {
      if (!keysToKeep.includes(key)) {
        localStorage.removeItem(key);
      }
    });
    
    console.log('로컬 스토리지 정리 완료 (알림 데이터 보호됨)');
    return { success: true, message: '로컬 스토리지 정리 완료 (알림 데이터 보호됨)' };
  }
};

// 현재 스토어 ID 가져오기 함수
const getCurrentStoreId = (): number => {
  return localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
};

// 인벤토리 API URL 가져오기
const getInventoryApiUrl = (): string => {
  return inventoryApiInstance.defaults.baseURL || '';
};

// 서비스 타입에 따른 API URL 가져오기
const getServiceApiUrl = (type: ApiType = 'GATEWAY'): string => {
  switch (type) {
    case 'GATEWAY':
      return api.defaults.baseURL || '';
    case 'INVENTORY':
      return inventoryApiInstance.defaults.baseURL || '';
    case 'ORDER':
      return orderApi.defaults.baseURL || '';
    default:
      return api.defaults.baseURL || '';
}
};

// 메뉴 API
const menuApi = {
  getAllMenus: async (storeId: number, params?: { 
  type?: string,
  isActive?: boolean,
    page?: number, 
    limit?: number,
    searchTerm?: string
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('storeId', storeId.toString());
  
    if (params) {
      if (params.type) queryParams.append('type', params.type);
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.searchTerm) queryParams.append('search', params.searchTerm);
    }
    
    try {
      // API 요청 URL 로깅
      const url = `/menus?${queryParams.toString()}`;
      console.log(`메뉴 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      // API 호출
      const response = await inventoryApiInstance.get(url);
      console.log('메뉴 API 응답 상태:', response.status);
      console.log('메뉴 API 응답 데이터 구조:', {
        hasData: !!response.data,
        dataType: typeof response.data,
        isArray: Array.isArray(response.data),
        dataKeys: response.data ? Object.keys(response.data) : [],
        hasMenus: response.data && response.data.menus ? true : false,
        menusType: response.data && response.data.menus ? typeof response.data.menus : null,
        isMenusArray: response.data && response.data.menus ? Array.isArray(response.data.menus) : null,
        menusLength: response.data && response.data.menus && Array.isArray(response.data.menus) ? response.data.menus.length : null,
        totalCount: response.data && response.data.totalCount ? response.data.totalCount : null,
        firstItem: response.data && Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null,
        firstMenusItem: response.data && response.data.menus && Array.isArray(response.data.menus) && response.data.menus.length > 0 ? response.data.menus[0] : null
      });
      console.log('메뉴 API 응답 데이터(전체):', response.data);
      return response;
    } catch (error) {
      console.error('메뉴 데이터 조회 오류:', error);
      throw error;
    }
  },
  
  getMenuById: async (id: number) => {
    try {
      console.log(`메뉴 상세정보 요청 URL: ${inventoryApiInstance.defaults.baseURL}/menus/${id}`);
      const response = await inventoryApiInstance.get(`/menus/${id}?includeCookingMethods=true`);
      console.log('메뉴 상세정보 응답:', response.data);
      return response;
    } catch (error) {
      console.error(`메뉴 ID ${id} 조회 오류:`, error);
      throw error;
    }
  },
  
  createMenu: async (menuData: any): Promise<any> => {
    try {
      // 백엔드 API 호출
      try {
        const response = await inventoryApiInstance.post('/menus', menuData);
      console.log('API 메뉴 생성 응답 상태:', response.status);
      console.log('API 메뉴 생성 응답 데이터:', response.data);
        return response.data;
    } catch (error: any) {
      console.error('메뉴 생성 오류:', error);
        throw error;
      }
    } catch (error: any) {
      console.error('메뉴 생성 최종 오류:', error);
      throw error;
    }
  },

  updateMenu: async (id: number, menu: any) => {
    try {
      // API 요청 URL 로깅
      const url = `/menus/${id}`;
      console.log(`메뉴 업데이트 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      // API 호출
      const response = await inventoryApiInstance.put(url, menu);
      return response;
    } catch (error) {
      console.error(`메뉴 ID ${id} 업데이트 오류:`, error);
      throw error;
    }
  },

  updateMenuStatus: async (id: number, status: any) => {
    try {
      const response = await inventoryApiInstance.patch(`/menus/${id}/status`, status);
      return response;
    } catch (error) {
      console.error(`메뉴 ID ${id} 상태 업데이트 오류:`, error);
      throw error;
    }
  },

  deleteMenu: async (id: number) => {
    try {
      const response = await inventoryApiInstance.delete(`/menus/${id}`);
      return response;
    } catch (error) {
      console.error(`메뉴 ID ${id} 삭제 오류:`, error);
      throw error;
    }
  }
};

// 작업관리 API
const workApi = {
  // 작업대 관리
  getWorkStations: async (storeId: number) => {
    try {
      // API 요청 URL 로깅
      const url = `/work-management/workstations?storeId=${storeId}`;
      console.log(`작업대 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      // API 호출
      const response = await inventoryApiInstance.get(url);
      console.log('API 작업대 응답 상태:', response.status);
      console.log('API 작업대 응답 데이터:', response.data);
      return response;
    } catch (error) {
      console.error('작업대 데이터 조회 오류:', error);
      throw error;
    }
  },

  createWorkStation: async (workStation: any) => {
    try {
      // API 요청 URL 로깅
      const url = '/work-management/workstations';
      console.log(`작업대 생성 API 요청 URL: ${inventoryApiInstance.defaults.baseURL}${url}`);
      
      // API 호출
      const response = await inventoryApiInstance.post(url, workStation);
      return response;
    } catch (error) {
      console.error('작업대 생성 오류:', error);
      throw error;
    }
  },

  updateWorkStation: async (id: number, workStation: any) => {
    try {
      const url = `/work-management/workstations/${id}`;
      const response = await inventoryApiInstance.put(url, workStation);
      return response;
    } catch (error) {
      console.error(`작업대 ID ${id} 업데이트 오류:`, error);
      throw error;
    }
  },

  deleteWorkStation: async (id: number) => {
    try {
      const storeId = localStorage.getItem('storeId') ? parseInt(localStorage.getItem('storeId')!) : 1;
      const url = `/work-management/workstations/${id}?storeId=${storeId}`;
      const response = await inventoryApiInstance.delete(url);
      return response;
    } catch (error) {
      console.error(`작업대 ID ${id} 삭제 오류:`, error);
      throw error;
    }
  }
};

// 새로운 발주서 API
const newPurchaseOrderApi = {
  // 발주서 목록 조회
  getPurchaseOrders: async (storeId: number, supplierId?: number, status?: NewPurchaseOrderStatus) => {
    const params = new URLSearchParams();
    params.append('storeId', storeId.toString());
    if (supplierId) params.append('supplierId', supplierId.toString());
    if (status !== undefined && status !== null) params.append('status', String(status));
    const url = `/purchase-orders?${params.toString()}`;
    return await inventoryApiInstance.get<NewPurchaseOrder[]>(url);
  },

  // 발주서 상세 조회
  getPurchaseOrderById: async (id: number) => {
    return await inventoryApiInstance.get<NewPurchaseOrder>(`/purchase-orders/${id}`);
  },

  // 발주서 생성
  createPurchaseOrder: async (request: CreatePurchaseOrderRequest) => {
    return await inventoryApiInstance.post<NewPurchaseOrder>('/purchase-orders', request);
  },

  // 발주서 상태 업데이트
  updatePurchaseOrderStatus: async (id: number, status: NewPurchaseOrderStatus) => {
    return await inventoryApiInstance.put<NewPurchaseOrder>(`/purchase-orders/${id}/status`, { status });
  },

  // 발주서 내용 수정
  updatePurchaseOrder: async (id: number, request: {
    items: Array<{
      ingredientId: number;
      ingredientName: string;
      requestedQuantity: number;
      requestedVolume?: number;
      unit: string;
      volumeUnit?: string;
    }>;
    notes?: string;
    orderDate?: string; // 발주 일시 업데이트용
  }) => {
    return await inventoryApiInstance.put<NewPurchaseOrder>(`/purchase-orders/${id}`, request);
  },

  // 입고 처리
  processInbound: async (id: number, request: ProcessInboundRequest) => {
    return await inventoryApiInstance.post<NewPurchaseOrder>(`/purchase-orders/${id}/inbound`, request);
  },

  // 거래처별 발주 가능 여부 확인
  canOrderForSupplier: async (supplierId: number, storeId: number) => {
    return await inventoryApiInstance.get<CanOrderResult>(`/purchase-orders/supplier/${supplierId}/can-order?storeId=${storeId}`);
  },

  // 거래처별 발주 항목 조회
  getOrderItemsForSupplier: async (supplierId: number, storeId: number) => {
    return await inventoryApiInstance.get<OrderItemInfo[]>(`/purchase-orders/supplier/${supplierId}/order-items?storeId=${storeId}`);
  }
};

// 대량 업로드 API
const bulkUploadApi = {
  // 템플릿 다운로드
  downloadTemplate: async (): Promise<void> => {
    try {
      const response = await api.get('/bulk-upload/template', {
        responseType: 'blob',
        timeout: 60000, // 60초 타임아웃
      });

      // Blob 객체 생성
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // 다운로드 URL 생성
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '대량업로드_양식.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // URL 해제
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('템플릿 다운로드 실패:', error);
      throw error;
    }
  },

  // 파일 업로드
  uploadFile: async (file: File): Promise<BulkUploadResponse> => {
    // 파일 크기 검증 (10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('파일 크기가 10MB를 초과합니다.');
    }

    // 파일 형식 검증
    const validExtensions = ['.xlsx', '.xls'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    if (!hasValidExtension) {
      throw new Error('지원하지 않는 파일 형식입니다. .xlsx 또는 .xls 파일만 업로드 가능합니다.');
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post<BulkUploadResponse>('/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 120초 타임아웃 (대용량 파일 고려)
      });

      return response.data;
    } catch (error: any) {
      console.error('파일 업로드 실패:', error);
      throw error;
    }
  },
};

// 모든 API 함수 export
export { 
  authApi, 
  storeApi, 
  multiStoreApi,
  ordersApi, 
  menuApi, 
  workApi, 
  supplyChainApi, 
  analyticsApi, 
  testApi,
  newPurchaseOrderApi,
  packagingContainerApi,
  packagingContainerTransactionApi,
  bulkUploadApi,
  getCurrentStoreId,
  getInventoryApiUrl,
  getServiceApiUrl
};

// ApiType 타입도 내보내기
export type { ApiType }; 